import React, { useState, useEffect, useMemo, useCallback } from "react";
import API from "../../utils/api";
import toast from "react-hot-toast";
import { useAuth } from "../../App";

// ── Mobile-responsive CSS ──────────────────────────────────────────────────
const MOBILE_CSS = `
  .an-2col          { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .an-company-grid  { display: grid; gap: 16px; }
  .an-kpi-grid      { display: grid; grid-template-columns: repeat(auto-fit, minmax(145px, 1fr)); gap: 10px; }
  .an-filter-row    { display: flex; gap: 12px; flex-wrap: wrap; align-items: flex-end; }
  .an-compare-cols  { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; }
  .an-drill-cols    { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
  .an-dark-sel { background:#1a1b2e !important; color:#f9fafb !important; color-scheme:dark; }
  .an-dark-sel option { background:#1a1b2e !important; color:#f9fafb !important; }
  input[type="date"].an-dark-sel { color-scheme:dark; }
  @media (max-width: 680px) {
    .an-2col          { grid-template-columns: 1fr !important; }
    .an-company-grid  { grid-template-columns: 1fr !important; }
    .an-compare-cols  { grid-template-columns: 1fr !important; }
    .an-drill-cols    { grid-template-columns: 1fr !important; }
    .an-kpi-grid      { grid-template-columns: repeat(2, 1fr) !important; }
    .an-filter-row    { gap: 8px !important; }
    .an-filter-row select, .an-filter-row input {
      font-size: 13px !important;
      padding: 7px 10px !important;
    }
  }
`;

// ── Constants ──────────────────────────────────────────────────────────────
const COMPANY_LABELS = { all: "All Companies", ebek: "Ebek", ebekmed: "Ebek Med" };
const COMPANY_ACCENT = { ebek: "#E31E24", ebekmed: "#2AB5A5", all: "#7C3AED" };
const COMPANY_BG     = { ebek: "#FEE2E2", ebekmed: "#CCFBF1" };
const MONTHS_LABEL   = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
const MONTHS_FULL    = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const CATEGORY_ICONS = { Travel:"🚂", Hotel:"🏨", Food:"🍽️", Fuel:"⛽", Courier:"📦", Stationeries:"📝", Prints:"🖨️", Miscellaneous:"📌" };
const CATEGORY_COLORS = {
  Travel:"#4f46e5", Hotel:"#0891b2", Food:"#059669", Fuel:"#d97706",
  Courier:"#7C3AED", Stationeries:"#E31E24", Prints:"#ec4899", Miscellaneous:"#6b7280",
};
const CAT_COLOR_LIST = ["#4f46e5","#0891b2","#059669","#d97706","#7C3AED","#E31E24","#ec4899","#6b7280"];

const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => currentYear - i);

// ── Helpers ────────────────────────────────────────────────────────────────
function fmtShort(n) {
  const v = parseFloat(n || 0);
  if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`;
  if (v >= 1000)   return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${Math.round(v)}`;
}
function fmt(n) { return parseFloat(n||0).toLocaleString("en-IN", { minimumFractionDigits: 2 }); }

function periodLabel(year, month, customFrom, customTo) {
  if (year === "custom") return customFrom && customTo ? `${new Date(customFrom).toLocaleDateString("en-IN")} – ${new Date(customTo).toLocaleDateString("en-IN")}` : "Custom Range";
  if (year === "last12") return "Last 12 Months";
  if (month) return `${MONTHS_FULL[parseInt(month) - 1]} ${year}`;
  return `${year} (YTD)`;
}

function getRange(year, month, customFrom, customTo) {
  // Custom date range
  if (year === "custom") return { from: customFrom, to: customTo };
  // Special rolling window: last 12 months
  if (year === "last12") {
    const today = new Date();
    const from  = new Date(today);
    from.setMonth(from.getMonth() - 12);
    from.setDate(1);
    return { from: from.toISOString().substring(0, 10), to: today.toISOString().substring(0, 10) };
  }
  if (month) {
    const m  = month.padStart(2, "0");
    const dy = new Date(parseInt(year), parseInt(month), 0).getDate();
    return { from: `${year}-${m}-01`, to: `${year}-${m}-${String(dy).padStart(2,"0")}` };
  }
  const today = new Date().toISOString().substring(0, 10);
  const start = `${year}-01-01`;
  return { from: start, to: year === String(currentYear) ? today : `${year}-12-31` };
}

// ── SVG: Donut Chart ───────────────────────────────────────────────────────
function polar(cx, cy, r, deg) {
  const rad = ((deg - 90) * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}
function arcPath(cx, cy, oR, iR, s, e) {
  const delta = Math.min(e - s, 359.9999);
  const end   = s + delta;
  const large = delta > 180 ? 1 : 0;
  const o1 = polar(cx,cy,oR,s), o2 = polar(cx,cy,oR,end);
  const i1 = polar(cx,cy,iR,end), i2 = polar(cx,cy,iR,s);
  return `M${o1.x},${o1.y} A${oR},${oR},0,${large},1,${o2.x},${o2.y} L${i1.x},${i1.y} A${iR},${iR},0,${large},0,${i2.x},${i2.y} Z`;
}

function DonutChart({ segments, size = 180, thickness = 36 }) {
  const [hov, setHov] = useState(null);
  const total = segments.reduce((s, d) => s + d.value, 0);
  if (!total) return (
    <div style={{ height: size, display:"flex", alignItems:"center", justifyContent:"center", color:"#9ca3af", fontSize:12 }}>
      No data
    </div>
  );
  const cx = size/2, cy = size/2, oR = size/2 - 6, iR = oR - thickness;
  let cum = 0;
  const segs = segments.map(d => {
    const start = cum * 360; cum += d.value / total; const end = cum * 360;
    return { ...d, start, end, pct: (d.value / total * 100).toFixed(1) };
  });
  const h = hov != null ? segs[hov] : null;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ overflow:"visible", flexShrink:0 }}>
      {segs.map((s,i) => (
        <path key={i} d={arcPath(cx,cy, hov===i ? oR+7 : oR, iR, s.start, s.end)}
          fill={s.color} opacity={hov!=null && hov!==i ? 0.35 : 1}
          style={{ cursor:"pointer", transition:"all 0.18s" }}
          onMouseEnter={() => setHov(i)} onMouseLeave={() => setHov(null)}
          onTouchStart={() => setHov(hov===i ? null : i)}
        />
      ))}
      <text x={cx} y={cy-7}  textAnchor="middle" fontSize={11} fontWeight="800" fill="#f9fafb">
        {h ? fmtShort(h.value) : fmtShort(total)}
      </text>
      <text x={cx} y={cy+10} textAnchor="middle" fontSize={9} fill={h ? h.color : "#d1d5db"} fontWeight="700">
        {h ? `${h.pct}%` : "Total"}
      </text>
      {h && <text x={cx} y={cy+25} textAnchor="middle" fontSize={8} fill={h.color}>{h.label}</text>}
    </svg>
  );
}

function DonutLegend({ segments, total, compact }) {
  return (
    <div style={{ display:"flex", flexDirection:"column", gap: compact ? 5 : 7 }}>
      {segments.map((s,i) => (
        <div key={i} style={{ display:"flex", alignItems:"center", gap:7 }}>
          <div style={{ width:9, height:9, borderRadius:2, background:s.color, flexShrink:0 }} />
          <span style={{ fontSize: compact?11:12, color:"#d1d5db", flex:1, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", minWidth:0 }}>
            {CATEGORY_ICONS[s.label]||"📌"} {s.label}
          </span>
          <span style={{ fontSize: compact?11:12, fontWeight:700, color:s.color, whiteSpace:"nowrap" }}>
            {fmtShort(s.value)}
          </span>
          <span style={{ fontSize:10, color:"#9ca3af", width:34, textAlign:"right", flexShrink:0 }}>
            {(s.value/total*100).toFixed(1)}%
          </span>
        </div>
      ))}
    </div>
  );
}

// ── SVG: Dual-line Trend Chart ─────────────────────────────────────────────
function TrendChart({ dataA, dataB, colorA="#7C3AED", colorB="#d97706", labelA="Period A", labelB="Period B" }) {
  const [hovIdx, setHovIdx] = useState(null);
  const hasB = dataB && dataB.length > 0;

  // Unify x-axis by month keys
  const allMonths = useMemo(() => {
    const keys = new Set([...(dataA||[]).map(d=>d.month), ...(dataB||[]).map(d=>d.month)]);
    return [...keys].sort();
  }, [dataA, dataB]);

  const ptsA = useMemo(() => allMonths.map(m => ({ month:m, total: (dataA||[]).find(d=>d.month===m)?.total || 0, label: MONTHS_LABEL[parseInt(m.substring(5,7))-1] })), [allMonths, dataA]);
  const ptsB = useMemo(() => allMonths.map(m => ({ month:m, total: (dataB||[]).find(d=>d.month===m)?.total || 0, label: MONTHS_LABEL[parseInt(m.substring(5,7))-1] })), [allMonths, dataB]);

  if (!allMonths.length) return (
    <div style={{ height:140, display:"flex", alignItems:"center", justifyContent:"center", color:"#9ca3af", fontSize:13 }}>
      Not enough data
    </div>
  );

  const W=540, H=170;
  const PAD={t:20,r:16,b:34,l:52};
  const iW=W-PAD.l-PAD.r, iH=H-PAD.t-PAD.b;
  const maxV = Math.max(...ptsA.map(d=>d.total), ...(hasB?ptsB.map(d=>d.total):[]), 1);

  const svgPt = (pts, i) => ({
    x: PAD.l + (pts.length===1 ? iW/2 : (i/(pts.length-1))*iW),
    y: PAD.t + iH - (pts[i].total/maxV)*iH,
  });
  const lineD = (pts) => pts.map((_, i) => `${i===0?"M":"L"}${svgPt(pts,i).x.toFixed(1)},${svgPt(pts,i).y.toFixed(1)}`).join(" ");
  const areaD = (pts) => {
    const d = lineD(pts);
    const n = pts.length-1;
    return `${d} L${svgPt(pts,n).x},${PAD.t+iH} L${svgPt(pts,0).x},${PAD.t+iH} Z`;
  };

  const gridY = [0,0.25,0.5,0.75,1].map(f=>({ y:PAD.t+iH-f*iH, label:f===0?"0":fmtShort(maxV*f) }));

  return (
    <div>
      {hasB && (
        <div style={{ display:"flex", gap:16, marginBottom:10, fontSize:12 }}>
          {[{color:colorA,label:labelA},{color:colorB,label:labelB}].map((l,i)=>(
            <div key={i} style={{ display:"flex", alignItems:"center", gap:6 }}>
              <div style={{ width:20, height:3, background:l.color, borderRadius:2 }} />
              <span style={{ color:"#d1d5db" }}>{l.label}</span>
            </div>
          ))}
        </div>
      )}
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", overflow:"visible" }}>
        <defs>
          <linearGradient id={`gA${colorA.replace("#","")}`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%"   stopColor={colorA} stopOpacity="0.25"/>
            <stop offset="100%" stopColor={colorA} stopOpacity="0.02"/>
          </linearGradient>
          {hasB && (
            <linearGradient id={`gB${colorB.replace("#","")}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={colorB} stopOpacity="0.18"/>
              <stop offset="100%" stopColor={colorB} stopOpacity="0.02"/>
            </linearGradient>
          )}
        </defs>
        {/* Grid */}
        {gridY.map((g,i)=>(
          <g key={i}>
            <line x1={PAD.l} y1={g.y} x2={W-PAD.r} y2={g.y} stroke="rgba(255,255,255,0.08)" strokeWidth={1}/>
            <text x={PAD.l-5} y={g.y+3.5} textAnchor="end" fontSize={9} fill="#d1d5db">{g.label}</text>
          </g>
        ))}
        {/* Area A */}
        {ptsA.length>1 && <path d={areaD(ptsA)} fill={`url(#gA${colorA.replace("#","")})`}/>}
        {/* Area B */}
        {hasB && ptsB.length>1 && <path d={areaD(ptsB)} fill={`url(#gB${colorB.replace("#","")})`}/>}
        {/* Line A */}
        <path d={lineD(ptsA)} fill="none" stroke={colorA} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"/>
        {/* Line B */}
        {hasB && <path d={lineD(ptsB)} fill="none" stroke={colorB} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" strokeDasharray="6,3"/>}
        {/* Points A */}
        {ptsA.map((_,i)=>{
          const p=svgPt(ptsA,i); const isH=hovIdx===i;
          return (
            <g key={i} onMouseEnter={()=>setHovIdx(i)} onMouseLeave={()=>setHovIdx(null)} style={{cursor:"default"}}>
              <circle cx={p.x} cy={p.y} r={isH?6:4} fill={colorA} stroke="white" strokeWidth={2} style={{transition:"r 0.15s"}}/>
              {isH && (
                <g>
                  <rect x={p.x-34} y={p.y-30} width={68} height={20} rx={4} fill={colorA}/>
                  <text x={p.x} y={p.y-17} textAnchor="middle" fontSize={9} fill="white" fontWeight="700">{fmtShort(ptsA[i].total)}</text>
                </g>
              )}
              <text x={p.x} y={H-5} textAnchor="middle" fontSize={9} fill="#d1d5db">{ptsA[i].label}</text>
            </g>
          );
        })}
        {/* Points B */}
        {hasB && ptsB.map((_,i)=>{
          const p=svgPt(ptsB,i); const isH=hovIdx===i;
          return (
            <g key={i} onMouseEnter={()=>setHovIdx(i)} onMouseLeave={()=>setHovIdx(null)} style={{cursor:"default"}}>
              <circle cx={p.x} cy={p.y} r={isH?6:4} fill={colorB} stroke="white" strokeWidth={2} style={{transition:"r 0.15s"}}/>
              {isH && (
                <g>
                  <rect x={p.x-34} y={p.y-50} width={68} height={20} rx={4} fill={colorB}/>
                  <text x={p.x} y={p.y-37} textAnchor="middle" fontSize={9} fill="white" fontWeight="700">{fmtShort(ptsB[i].total)}</text>
                </g>
              )}
            </g>
          );
        })}
      </svg>
    </div>
  );
}

// ── Category Compare Bars ──────────────────────────────────────────────────
function CategoryCompare({ catA, catB, labelA, labelB }) {
  const allCats = [...new Set([...Object.keys(catA), ...Object.keys(catB)])]
    .filter(c => (catA[c]||0)+(catB[c]||0) > 0)
    .sort((a,b) => ((catB[b]||0)+(catA[b]||0)) - ((catA[a]||0)+(catB[a]||0)));
  const maxV = Math.max(...allCats.map(c => Math.max(catA[c]||0, catB[c]||0)), 1);
  const COL_A = "#7C3AED", COL_B = "#d97706";

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      <div style={{ display:"flex", gap:16, fontSize:12 }}>
        {[{c:COL_A,l:labelA},{c:COL_B,l:labelB}].map((x,i)=>(
          <div key={i} style={{ display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ width:18, height:4, borderRadius:2, background:x.c }}/>
            <span style={{ color:"#d1d5db", fontWeight:600 }}>{x.l}</span>
          </div>
        ))}
      </div>
      {allCats.map(cat => {
        const a = catA[cat]||0, b = catB[cat]||0;
        const delta = a>0 ? ((b-a)/a*100).toFixed(0) : null;
        const up = b>a;
        return (
          <div key={cat}>
            <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5, fontSize:12 }}>
              <span style={{ fontWeight:600, color:"#f9fafb" }}>{CATEGORY_ICONS[cat]||"📌"} {cat}</span>
              {delta!==null && (
                <span style={{ color:up?"#DC2626":"#059669", fontWeight:700, fontSize:11 }}>
                  {up?"↑":"↓"}{Math.abs(delta)}%
                </span>
              )}
            </div>
            {/* Bar A */}
            <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:3 }}>
              <div style={{ width:56, fontSize:10, color:COL_A, fontWeight:700, textAlign:"right", flexShrink:0 }}>{fmtShort(a)}</div>
              <div style={{ flex:1, height:14, background:"rgba(255,255,255,0.1)", borderRadius:4, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${(a/maxV)*100}%`, background:`linear-gradient(90deg,${COL_A}99,${COL_A})`, borderRadius:4, minWidth:a>0?4:0, transition:"width 0.5s" }}/>
              </div>
            </div>
            {/* Bar B */}
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ width:56, fontSize:10, color:COL_B, fontWeight:700, textAlign:"right", flexShrink:0 }}>{fmtShort(b)}</div>
              <div style={{ flex:1, height:14, background:"rgba(255,255,255,0.1)", borderRadius:4, overflow:"hidden" }}>
                <div style={{ height:"100%", width:`${(b/maxV)*100}%`, background:`linear-gradient(90deg,${COL_B}99,${COL_B})`, borderRadius:4, minWidth:b>0?4:0, transition:"width 0.5s" }}/>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Aggregate helpers ──────────────────────────────────────────────────────
// ── Food subcategory constants ─────────────────────────────────────────────
const FOOD_SUB_COLORS = { BF: "#0891b2", Lunch: "#059669", Dinner: "#7C3AED" };
const FOOD_SUB_ICONS  = { BF: "☕", Lunch: "🥗", Dinner: "🍽️" };
const FOOD_SUB_ORDER  = ["BF", "Lunch", "Dinner"];

function aggregateRows(rows) {
  const cats={}, emps={}, months={}, comps={}, trips={};
  const cashRows = rows.filter(r=>r.bill_type==="cash_voucher");
  const foodSub  = {}; // { BF: total, Lunch: total, Dinner: total }

  rows.forEach(r => {
    const amt = parseFloat(r.amount||0);
    const co  = r.employee_company;

    // Categories
    cats[r.category] = (cats[r.category]||0) + amt;

    // Food subcategory aggregation
    if (r.category === "Food" && r.food_subcategory) {
      const sub = r.food_subcategory;
      foodSub[sub] = (foodSub[sub]||0) + amt;
    }

    // Employees
    if (!emps[r.employee_name]) emps[r.employee_name] = { name:r.employee_name, company:co, dept:r.department, total:0, categories:{}, trips:{}, foodSub:{} };
    emps[r.employee_name].total += amt;
    emps[r.employee_name].categories[r.category] = (emps[r.employee_name].categories[r.category]||0) + amt;
    if (r.category === "Food" && r.food_subcategory) {
      const sub = r.food_subcategory;
      emps[r.employee_name].foodSub[sub] = (emps[r.employee_name].foodSub[sub]||0) + amt;
    }
    if (r.trip_id && !emps[r.employee_name].trips[r.trip_id]) {
      emps[r.employee_name].trips[r.trip_id] = { id:r.trip_id, title:r.trip_title, dest:r.destination, advance:parseFloat(r.advance_amount||0), spent:parseFloat(r.total_expenses||0), status:r.trip_status };
    }

    // Monthly
    const month = String(r.expense_date||"").substring(0,7);
    if (month) months[month] = (months[month]||0) + amt;

    // Company
    if (!comps[co]) comps[co] = { name:COMPANY_LABELS[co]||co, color:COMPANY_ACCENT[co]||"#6b7280", total:0, categories:{} };
    comps[co].total += amt;
    comps[co].categories[r.category] = (comps[co].categories[r.category]||0) + amt;

    // Trips
    if (r.trip_id && !trips[r.trip_id]) {
      trips[r.trip_id] = { id:r.trip_id, title:r.trip_title, employee:r.employee_name, company:co, dest:r.destination, advance:parseFloat(r.advance_amount||0), spent:parseFloat(r.total_expenses||0), status:r.trip_status };
    }
  });

  const totalSpent = rows.reduce((s,r)=>s+parseFloat(r.amount||0), 0);
  const cashAmt    = cashRows.reduce((s,r)=>s+parseFloat(r.amount||0), 0);

  // Employees who have any food expenses, sorted most→least food spend
  const foodEmpList = Object.values(emps)
    .filter(e => Object.values(e.foodSub).some(v=>v>0))
    .sort((a,b) => Object.values(b.foodSub).reduce((s,v)=>s+v,0) - Object.values(a.foodSub).reduce((s,v)=>s+v,0));

  return {
    catSegs: Object.entries(cats).sort((a,b)=>b[1]-a[1]).map(([label,value],i)=>({ label, value, color:CATEGORY_COLORS[label]||CAT_COLOR_LIST[i%CAT_COLOR_LIST.length] })),
    empList: Object.values(emps).sort((a,b)=>b.total-a.total),
    monthList: Object.entries(months).sort(([a],[b])=>a.localeCompare(b)).map(([month,total])=>({ month, total, label:MONTHS_LABEL[parseInt(month.substring(5,7))-1] })),
    compList: Object.values(comps).sort((a,b)=>b.total-a.total),
    tripList: Object.values(trips).filter(t=>t.advance>0||t.spent>0),
    totalSpent, cashAmt, cashCnt:cashRows.length, totalCnt:rows.length,
    catMap: cats,
    foodSub,      // { BF: amt, Lunch: amt, Dinner: amt }
    foodEmpList,  // employees sorted by total food spend
  };
}

// ── Main Analytics Page ────────────────────────────────────────────────────
export default function Analytics() {
  const { user } = useAuth();
  const isSuperadmin = user?.role === "superadmin" || user?.role === "executive";

  // ── Main filters ──────────────────────────────────────────────────────────
  const [company,      setCompany]      = useState(isSuperadmin ? "all" : (user?.company || "ebek"));
  const [year,         setYear]         = useState(String(currentYear)); // default: current year so month selector is visible
  const [month,        setMonth]        = useState(String(new Date().getMonth()+1).padStart(2,"0")); // current month
  const [tripCategory, setTripCategory] = useState(""); // "" = all categories
  const [customFrom,   setCustomFrom]   = useState("");
  const [customTo,     setCustomTo]     = useState("");
  const [empFilter,    setEmpFilter]    = useState(""); // "" = all employees

  const EBEK_CATEGORIES = [
    "OET Examination",
    "Cambridge Examination",
    "Other Examination",
    "Marketing",
    "Others",
  ];

  const EBEKMED_CATEGORIES = [
    "INC Training",
    "OSCE Training",
    "TOT Training",
    "Marketing",
    "Petty Cash",
    "Others",
  ];

  // Categories to show in the Trip Category dropdown based on selected company
  const tripCategoryOptions = useMemo(() => {
    if (company === "ebekmed") return EBEKMED_CATEGORIES;
    if (company === "ebek")    return EBEK_CATEGORIES;
    // "all" (superadmin) — merge both lists, deduplicate, keep order
    return [...new Set([...EBEK_CATEGORIES, ...EBEKMED_CATEGORIES])];
  }, [company]);

  // ── Compare mode ──────────────────────────────────────────────────────────
  const [compareMode,    setCompareMode]    = useState(false);
  const [compCompany,    setCompCompany]    = useState(isSuperadmin ? "all" : (user?.company || "ebek"));
  const [compYear,       setCompYear]       = useState(String(currentYear));
  const [compMonth,      setCompMonth]      = useState("");

  // ── Data ──────────────────────────────────────────────────────────────────
  const [data,     setData]     = useState(null);
  const [compData, setCompData] = useState(null);
  const [loading,  setLoading]  = useState(false);
  const [compLoading, setCompLoading] = useState(false);
  const [drillEmp, setDrillEmp] = useState(null);

  // ── Tab navigation ────────────────────────────────────────────────────────
  const [activeTab, setActiveTab] = useState("spend");

  // ── Budget vs Actual tab data ─────────────────────────────────────────────
  const [budgetData, setBudgetData]           = useState(null);
  const [budgetLoading, setBudgetLoading]     = useState(false);
  const [budgetOverageNoteMode, setBudgetOverageNoteMode] = useState(null);
  const [budgetOverageNote, setBudgetOverageNote]         = useState("");
  const [budgetOverageProcessing, setBudgetOverageProcessing] = useState(null);

  // ── OET Analytics data ────────────────────────────────────────────────────
  const [oetData,    setOetData]    = useState(null);
  const [oetLoading, setOetLoading] = useState(false);

  useEffect(() => {
    if (activeTab === "oet" && !oetData && isSuperadmin) {
      setOetLoading(true);
      API.get("/oet-exams/analytics")
        .then(r => setOetData(r.data))
        .catch(err => toast.error("OET analytics error: " + (err?.response?.data?.error || err.message || "Failed to load")))
        .finally(() => setOetLoading(false));
    }
  }, [activeTab, oetData, isSuperadmin]);

  // ── Insight tab data ──────────────────────────────────────────────────────
  const [integrityData,   setIntegrityData]   = useState(null);
  const [auditTrendData,  setAuditTrendData]  = useState(null);
  const [agingData,       setAgingData]       = useState(null);
  const [utilizationData, setUtilizationData] = useState(null);
  const [reimbPipeData,   setReimbPipeData]   = useState(null);
  const [managerPerfData, setManagerPerfData] = useState(null);
  const [tripPurposeData, setTripPurposeData] = useState(null);
  const [complianceTrend, setComplianceTrend] = useState(null);
  const [billCorrData,    setBillCorrData]    = useState(null);
  const [insightLoading,  setInsightLoading]  = useState(false);

  // ── Fetch main data ───────────────────────────────────────────────────────
  const fetchMain = useCallback(async (co, yr, mo, cfrom="", cto="") => {
    if (yr === "custom" && (!cfrom || !cto)) return;
    setLoading(true);
    setDrillEmp(null);
    setEmpFilter("");
    const { from, to } = getRange(yr, mo, cfrom, cto);
    try {
      const params = `from=${from}&to=${to}&company=${co}`;
      const [rowsRes, summaryRes] = await Promise.all([
        API.get(`/reports/expenses?${params}`).catch(() => ({ data: [] })),
        API.get(`/reports/summary?${params}`).catch(() => ({ data: {} })),
      ]);
      setData({ rows: rowsRes.data || [], summary: summaryRes.data || {}, from, to, company: co, year: yr, month: mo });
    } catch { toast.error("Failed to load analytics"); }
    finally { setLoading(false); }
  }, []);

  const fetchCompare = useCallback(async (co, yr, mo) => {
    setCompLoading(true);
    const { from, to } = getRange(yr, mo);
    try {
      const params = `from=${from}&to=${to}&company=${co}`;
      const [rowsRes, summaryRes] = await Promise.all([
        API.get(`/reports/expenses?${params}`).catch(() => ({ data: [] })),
        API.get(`/reports/summary?${params}`).catch(() => ({ data: {} })),
      ]);
      setCompData({ rows: rowsRes.data || [], summary: summaryRes.data || {}, from, to, company: co, year: yr, month: mo });
    } catch { toast.error("Failed to load comparison data"); }
    finally { setCompLoading(false); }
  }, []);

  // ── Fetch all insight tab data ────────────────────────────────────────────
  const fetchInsights = useCallback(async (co, yr, mo, cfrom="", cto="") => {
    if (yr === "custom" && (!cfrom || !cto)) return;
    setInsightLoading(true);
    const { from, to } = getRange(yr, mo, cfrom, cto);
    const params = `from=${from}&to=${to}&company=${co}`;
    try {
      const [intRes, auditRes, agingRes, utilRes, reimbRes, mgrRes, purpRes, compRes, corrRes] = await Promise.all([
        API.get(`/reports/employee-integrity?${params}`).catch(()=>({data:[]})),
        API.get(`/reports/audit-trend?${params}`).catch(()=>({data:[]})),
        API.get(`/reports/settlement-aging?${params}`).catch(()=>({data:[]})),
        API.get(`/reports/advance-utilization?${params}`).catch(()=>({data:[]})),
        API.get(`/reports/reimbursement-pipeline?${params}`).catch(()=>({data:[]})),
        API.get(`/reports/manager-performance?${params}`).catch(()=>({data:[]})),
        API.get(`/reports/trip-purpose?${params}`).catch(()=>({data:[]})),
        API.get(`/reports/compliance-trend?${params}`).catch(()=>({data:[]})),
        API.get(`/reports/bill-corrections?${params}`).catch(()=>({data:[]})),
      ]);
      setIntegrityData(intRes.data);
      setAuditTrendData(auditRes.data);
      setAgingData(agingRes.data);
      setUtilizationData(utilRes.data);
      setReimbPipeData(reimbRes.data);
      setManagerPerfData(mgrRes.data);
      setTripPurposeData(purpRes.data);
      setComplianceTrend(compRes.data);
      setBillCorrData(corrRes.data);
    } catch { toast.error("Failed to load insights"); }
    finally { setInsightLoading(false); }
  }, []);

  const fetchBudget = useCallback(async (co, yr, mo, cfrom="", cto="") => {
    if (yr === "custom" && (!cfrom || !cto)) return;
    setBudgetLoading(true);
    const { from, to } = getRange(yr, mo, cfrom, cto);
    const params = new URLSearchParams({ from_date: from, to_date: to });
    if (co !== "all") params.append("company", co);
    try {
      const r = await API.get(`/trips/projections/summary?${params}`);
      setBudgetData(r.data);
    } catch { toast.error("Failed to load budget data"); }
    finally { setBudgetLoading(false); }
  }, []);

  // Auto-load on mount and when filters change (not for custom — user must click Refresh)
  useEffect(() => { if (year !== "custom") fetchMain(company, year, month); }, [company, year, month]);
  useEffect(() => { if (compareMode) fetchCompare(compCompany, compYear, compMonth); }, [compareMode, compCompany, compYear, compMonth]);

  // Clear insight data when filters change so it re-fetches on tab visit
  useEffect(() => {
    setIntegrityData(null); setAuditTrendData(null); setAgingData(null);
    setUtilizationData(null); setReimbPipeData(null); setManagerPerfData(null);
    setTripPurposeData(null); setComplianceTrend(null); setBillCorrData(null);
    setBudgetData(null);
  }, [company, year, month]);

  // Auto-fetch insights when switching to a non-spend tab
  useEffect(() => {
    if (activeTab !== "spend" && !integrityData && year !== "custom") {
      fetchInsights(company, year, month, customFrom, customTo);
    }
    if (activeTab === "budget" && !budgetData && year !== "custom") {
      fetchBudget(company, year, month, customFrom, customTo);
    }
  }, [activeTab]);

  // ── Aggregations ──────────────────────────────────────────────────────────
  // All unique employee names from raw data (for employee filter dropdown)
  const empNames = useMemo(() => !data ? [] : [...new Set(data.rows.map(r=>r.employee_name).filter(Boolean))].sort(), [data]);

  // Filter rows by trip category and employee filter
  const filteredRows = useMemo(() => {
    if (!data) return [];
    let rows = data.rows;
    if (tripCategory) rows = rows.filter(r => String(r.trip_title||"").toLowerCase().includes(tripCategory.toLowerCase()));
    if (empFilter) rows = rows.filter(r => r.employee_name === empFilter);
    return rows;
  }, [data, tripCategory, empFilter]);

  const agg  = useMemo(() => data     ? aggregateRows(filteredRows)  : null, [data, filteredRows]);
  const cAgg = useMemo(() => compData ? aggregateRows(compData.rows) : null, [compData]);

  // Employee-by-category comparison (superadmin, Ebek company)
  const empCategoryData = useMemo(() => {
    if (!data || !tripCategory) return [];
    const byEmp = {};
    data.rows
      .filter(r => String(r.trip_title||"").toLowerCase().includes(tripCategory.toLowerCase()))
      .forEach(r => {
        const name = r.employee_name || "Unknown";
        byEmp[name] = (byEmp[name]||0) + parseFloat(r.amount||0);
      });
    return Object.entries(byEmp)
      .sort(([,a],[,b]) => b-a)
      .map(([name, total]) => ({ name, total }));
  }, [data, tripCategory]);

  // Derived KPIs (respect empFilter for advance total)
  const filteredSummary = useMemo(() => !data ? [] : empFilter ? data.summary.filter(s=>s.employee_name===empFilter) : data.summary, [data, empFilter]);
  const totalAdv      = filteredSummary.reduce((s,r)=>s+parseFloat(r.total_advance||0),0);
  const advUtil       = totalAdv>0 && agg ? Math.min(agg.totalSpent/totalAdv*100,999).toFixed(1) : 0;
  const cashPct       = agg && agg.totalSpent>0 ? (agg.cashAmt/agg.totalSpent*100).toFixed(1) : 0;
  const overspendTrips = agg ? agg.tripList.filter(t=>t.spent>t.advance) : [];

  // Compare KPI delta
  const compTotalAdv  = compData ? compData.summary.reduce((s,r)=>s+parseFloat(r.total_advance||0),0) : 0;
  const delta = (a,b) => b>0 ? ((a-b)/b*100).toFixed(1) : null;

  // Employee drilldown
  const drillData   = drillEmp && agg ? agg.empList.find(e=>e.name===drillEmp) : null;
  const drillSegs   = drillData ? Object.entries(drillData.categories).sort((a,b)=>b[1]-a[1]).map(([cat,val],i)=>({ label:cat, value:val, color:CATEGORY_COLORS[cat]||CAT_COLOR_LIST[i%CAT_COLOR_LIST.length] })) : [];

  const maxEmpSpend = agg?.empList[0]?.total || 1;

  // ── Employee Compare state ─────────────────────────────────────────────────
  const [empCompareOpen,   setEmpCompareOpen]   = useState(false);
  const [empCompareMode,   setEmpCompareMode]   = useState("single"); // "single" | "all"
  const [empCompareSubMode,setEmpCompareSubMode] = useState("month"); // "month" | "trip"
  const [empCompareName,   setEmpCompareName]   = useState("");

  // Data for single-employee month-on-month
  const singleEmpMonths = useMemo(() => {
    if (!data || !empCompareName) return [];
    const byMonth = {};
    data.rows.filter(r => r.employee_name === empCompareName).forEach(r => {
      const m = String(r.expense_date||"").substring(0,7);
      if (m) byMonth[m] = (byMonth[m]||0) + parseFloat(r.amount||0);
    });
    return Object.entries(byMonth).sort(([a],[b])=>a.localeCompare(b)).map(([month,total])=>({ month, total, label:MONTHS_LABEL[parseInt(month.substring(5,7))-1] }));
  }, [data, empCompareName]);

  // Data for all-employees month-on-month (top 6 employees)
  const allEmpMonths = useMemo(() => {
    if (!agg || !data) return [];
    const top6 = agg.empList.slice(0, 6);
    return top6.map((emp, idx) => {
      const byMonth = {};
      data.rows.filter(r => r.employee_name === emp.name).forEach(r => {
        const m = String(r.expense_date||"").substring(0,7);
        if (m) byMonth[m] = (byMonth[m]||0) + parseFloat(r.amount||0);
      });
      const color = CAT_COLOR_LIST[idx % CAT_COLOR_LIST.length];
      return { name: emp.name, color, months: Object.entries(byMonth).sort(([a],[b])=>a.localeCompare(b)).map(([month,total])=>({ month, total, label:MONTHS_LABEL[parseInt(month.substring(5,7))-1] })) };
    });
  }, [agg, data]);

  const card = (extra={}) => ({
    background:"var(--bg-card)", borderRadius:14, border:"1px solid var(--border)", padding:"18px 20px", ...extra,
  });
  const selStyle = {
    padding:"8px 12px", borderRadius:8, border:"1.5px solid rgba(255,255,255,0.2)",
    fontSize:13, background:"#1a1b2e", color:"#f9fafb", fontFamily:"inherit", colorScheme:"dark",
  };
  const selClass = "an-dark-sel";

  const labelA = periodLabel(year, month) + (isSuperadmin ? ` · ${COMPANY_LABELS[company]||company}` : "");
  const labelB = compData ? periodLabel(compYear, compMonth) + (isSuperadmin ? ` · ${COMPANY_LABELS[compCompany]||compCompany}` : "") : "";

  return (
    <>
      <style>{MOBILE_CSS}</style>
      <div>

        {/* Header */}
        <div style={{ marginBottom:20 }}>
          <h1 className="page-title">📈 Analytics Dashboard</h1>
          <p className="page-subtitle">Real-time spend insights — auto-updates when you change filters</p>
        </div>

        {/* ── Filter Bar ────────────────────────────────────────────── */}
        <div className="card" style={{ marginBottom:20 }}>
          <div className="an-filter-row">
            {/* Company (superadmin only) */}
            {isSuperadmin && (
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:5 }}>Company</div>
                <select value={company} onChange={e=>{ setCompany(e.target.value); setTripCategory(""); }} style={selStyle} className={selClass}>
                  <option value="all">🏢 All Companies</option>
                  <option value="ebek">Ebek</option>
                  <option value="ebekmed">Ebek Med</option>
                </select>
              </div>
            )}
            {/* Year */}
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:5 }}>Period</div>
              <select value={year} onChange={e=>{ setYear(e.target.value); setMonth(""); }} style={selStyle} className={selClass}>
                <option value="last12">📅 Last 12 Months</option>
                {YEARS.map(y=><option key={y} value={y}>{y} (Full Year)</option>)}
                <option value="custom">📆 Custom Date Range</option>
              </select>
            </div>
            {/* Month — hidden when rolling 12-month or custom view */}
            {year !== "last12" && year !== "custom" && (
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:5 }}>Month</div>
              <select value={month} onChange={e=>setMonth(e.target.value)} style={selStyle} className={selClass}>
                <option value="">All Months (YTD)</option>
                {MONTHS_FULL.map((m,i)=>(
                  <option key={i} value={String(i+1).padStart(2,"0")}>{m}</option>
                ))}
              </select>
            </div>
            )}
            {/* Custom date range pickers */}
            {year === "custom" && (
              <>
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:5 }}>From Date</div>
                  <input type="date" value={customFrom} onChange={e=>setCustomFrom(e.target.value)} style={{ ...selStyle, width:150 }} className={selClass}/>
                </div>
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:5 }}>To Date</div>
                  <input type="date" value={customTo} onChange={e=>setCustomTo(e.target.value)} style={{ ...selStyle, width:150 }} className={selClass}/>
                </div>
              </>
            )}
            {/* Trip Category filter — visible for all companies, options vary by company */}
            {(isSuperadmin || user?.company === "ebek" || user?.company === "ebekmed") && (
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:5 }}>Trip Category</div>
                <select value={tripCategory} onChange={e=>{ setTripCategory(e.target.value); }} style={selStyle} className={selClass}>
                  <option value="">All Categories</option>
                  {tripCategoryOptions.map(c=><option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            )}
            {/* Employee filter */}
            {empNames.length > 0 && (
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:5 }}>Employee</div>
                <select className={selClass} value={empFilter} onChange={e=>{ setEmpFilter(e.target.value); setDrillEmp(null); }} style={selStyle}>
                  <option value="">👥 All Employees</option>
                  {empNames.map(n=><option key={n} value={n}>👤 {n}</option>)}
                </select>
              </div>
            )}
            <div style={{ flex:1 }}/>
            {/* Refresh (always visible; required for custom range) */}
            <div style={{ display:"flex", alignItems:"flex-end" }}>
              <button
                onClick={()=>{ fetchMain(company, year, month, customFrom, customTo); if(activeTab==="budget") fetchBudget(company, year, month, customFrom, customTo); else if(activeTab!=="spend") fetchInsights(company, year, month, customFrom, customTo); }}
                disabled={year==="custom"&&(!customFrom||!customTo)}
                style={{
                  padding:"9px 18px", borderRadius:8, border:"2px solid #059669",
                  background:"#059669", color:"white",
                  fontWeight:700, fontSize:13, cursor:"pointer", transition:"all 0.2s",
                  opacity:year==="custom"&&(!customFrom||!customTo)?0.5:1,
                }}>
                🔄 Refresh
              </button>
            </div>
            {/* Compare toggle */}
            <div style={{ display:"flex", alignItems:"flex-end" }}>
              <button
                onClick={()=>{ setCompareMode(!compareMode); if(compareMode) setCompData(null); }}
                style={{
                  padding:"9px 18px", borderRadius:8, border:`2px solid ${compareMode?"#7C3AED":"var(--border)"}`,
                  background:compareMode?"#7C3AED":"var(--bg-card)", color:compareMode?"white":"var(--text-muted)",
                  fontWeight:700, fontSize:13, cursor:"pointer", transition:"all 0.2s",
                }}>
                ⚖️ {compareMode ? "Comparing ON" : "Compare Periods"}
              </button>
            </div>
          </div>

          {/* Period label */}
          {data && !loading && (
            <div style={{ marginTop:12, fontSize:12, color:"#9ca3af", display:"flex", alignItems:"center", gap:6, flexWrap:"wrap" }}>
              <span>📅</span>
              <span>Showing: <strong style={{ color:"var(--text-primary)" }}>{labelA}</strong></span>
              {data.from && <span>· {new Date(data.from).toLocaleDateString("en-IN")} — {new Date(data.to).toLocaleDateString("en-IN")}</span>}
              {empFilter && (
                <span style={{ display:"flex", alignItems:"center", gap:6, padding:"2px 10px", background:"rgba(129,140,248,0.15)", border:"1px solid rgba(129,140,248,0.35)", borderRadius:20, color:"#818cf8", fontWeight:700 }}>
                  👤 {empFilter}
                  <button onClick={()=>setEmpFilter("")} style={{ background:"none", border:"none", color:"#818cf8", cursor:"pointer", fontSize:13, lineHeight:1, padding:0 }}>✕</button>
                </span>
              )}
            </div>
          )}
          {year === "custom" && (!customFrom || !customTo) && (
            <div style={{ marginTop:8, fontSize:12, color:"#d97706" }}>⚠️ Select both From and To dates, then click Refresh.</div>
          )}
        </div>

        {/* ── Compare Period B Selector ──────────────────────────────── */}
        {compareMode && (
          <div className="card" style={{ marginBottom:20, border:"2px solid #d97706", background:"#FFFBEB" }}>
            <div style={{ fontSize:12, fontWeight:800, color:"#d97706", marginBottom:12 }}>⚖️ Compare Against</div>
            <div className="an-filter-row">
              {isSuperadmin && (
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:5 }}>Company</div>
                  <select value={compCompany} onChange={e=>setCompCompany(e.target.value)} style={selStyle} className={selClass}>
                    <option value="all">🏢 All Companies</option>
                    <option value="ebek">Ebek</option>
                    <option value="ebekmed">Ebek Med</option>
                  </select>
                </div>
              )}
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:5 }}>Year</div>
                <select value={compYear} onChange={e=>setCompYear(e.target.value)} style={selStyle} className={selClass}>
                  {YEARS.map(y=><option key={y} value={y}>{y}</option>)}
                </select>
              </div>
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:5 }}>Month</div>
                <select value={compMonth} onChange={e=>setCompMonth(e.target.value)} style={selStyle} className={selClass}>
                  <option value="">All Months (YTD)</option>
                  {MONTHS_FULL.map((m,i)=>(
                    <option key={i} value={String(i+1).padStart(2,"0")}>{m}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        )}

        {/* ── Tab Navigation ────────────────────────────────────────── */}
        <div style={{ display:"flex", gap:4, marginBottom:20, borderBottom:"2px solid var(--border)", overflowX:"auto" }}>
          {[
            ["oet",         "🏫", "OET Costing"],
            ["spend",       "📊", "Spend"],
            ["budget",      "🎯", "Budget vs Actual"],
            ["compliance",  "🔍", "Compliance"],
            ["operations",  "⏱️", "Operations"],
            ["performance", "📋", "Performance"],
          ].map(([key, icon, label]) => (
            <button key={key} onClick={() => setActiveTab(key)}
              style={{
                padding:"10px 20px", border:"none", whiteSpace:"nowrap",
                background: "transparent",
                color: activeTab===key ? "#7C3AED" : "#9ca3af",
                fontWeight: activeTab===key ? 800 : 600,
                fontSize:13, cursor:"pointer",
                borderBottom: activeTab===key ? "3px solid #7C3AED" : "3px solid transparent",
                transition:"all 0.18s", marginBottom:"-2px",
              }}>
              {icon} {label}
            </button>
          ))}
        </div>

        {/* ── Loading State ──────────────────────────────────────────── */}
        {loading && (
          <div style={{ textAlign:"center", padding:"60px 20px" }}>
            <div style={{ fontSize:36, marginBottom:12 }}>⏳</div>
            <div style={{ color:"#9ca3af", fontSize:14 }}>Loading analytics...</div>
          </div>
        )}

        {/* ── No Data State ──────────────────────────────────────────── */}
        {!loading && data && data.rows.length === 0 && (
          <div style={{ textAlign:"center", padding:"60px 20px" }}>
            <div style={{ fontSize:48, marginBottom:12 }}>📭</div>
            <div style={{ color:"#d1d5db", fontSize:16, fontWeight:700 }}>No expenses found</div>
            <div style={{ color:"#9ca3af", fontSize:13, marginTop:6 }}>Try changing the year or month filter</div>
          </div>
        )}

        {/* ══════════════════════ OET COSTING TAB ══════════════════════ */}
        {activeTab === "oet" && (
          <div>
            {oetLoading && (
              <div style={{ textAlign:"center", padding:"60px 20px" }}>
                <div style={{ fontSize:36, marginBottom:12 }}>⏳</div>
                <div style={{ color:"#9ca3af", fontSize:14 }}>Loading OET analytics…</div>
              </div>
            )}
            {!oetLoading && (!oetData || !oetData.by_center?.length) && (
              <div style={{ textAlign:"center", padding:"60px 20px" }}>
                <div style={{ fontSize:48, marginBottom:12 }}>🏫</div>
                <div style={{ color:"#9ca3af", fontSize:15, fontWeight:600 }}>No OET exam data yet</div>
                <div style={{ color:"#64748b", fontSize:13, marginTop:6 }}>Create exam sessions in the OET Costing page to see analytics here.</div>
              </div>
            )}
            {!oetLoading && oetData && oetData.by_center?.length > 0 && (
              <OETAnalyticsContent data={oetData} />
            )}
          </div>
        )}

        {/* ── Main Analytics Content ─────────────────────────────────── */}
        {!loading && agg && data.rows.length > 0 && activeTab === "spend" && (
          <>
            {/* ── Compare View ──────────────────────────────────────── */}
            {compareMode && cAgg && (
              <div style={{ marginBottom:20 }}>
                {/* Compare KPI delta */}
                <div style={{ ...card(), marginBottom:16 }}>
                  <div style={{ fontSize:13, fontWeight:800, color:"var(--text-primary)", marginBottom:16 }}>
                    ⚖️ Period Comparison
                  </div>
                  <div className="an-compare-cols">
                    {/* Period A */}
                    <div style={{ padding:"14px 16px", borderRadius:12, background:"#F5F3FF", border:"2px solid #7C3AED30" }}>
                      <div style={{ fontSize:11, fontWeight:700, color:"#7C3AED", textTransform:"uppercase", marginBottom:8 }}>Period A · {labelA}</div>
                      {[
                        { label:"Total Spend",   value:fmtShort(agg.totalSpent),   color:"#E31E24" },
                        { label:"Advance",       value:fmtShort(totalAdv),          color:"#d97706" },
                        { label:"Transactions",  value:agg.totalCnt,                color:"#4f46e5" },
                        { label:"Employees",     value:agg.empList.length,          color:"#059669" },
                      ].map(k=>(
                        <div key={k.label} style={{ display:"flex", justifyContent:"space-between", marginBottom:6 }}>
                          <span style={{ fontSize:12, color:"#9ca3af" }}>{k.label}</span>
                          <span style={{ fontSize:13, fontWeight:800, color:k.color }}>{k.value}</span>
                        </div>
                      ))}
                    </div>
                    {/* Period B */}
                    <div style={{ padding:"14px 16px", borderRadius:12, background:"#FFFBEB", border:"2px solid #d9780630" }}>
                      <div style={{ fontSize:11, fontWeight:700, color:"#d97706", textTransform:"uppercase", marginBottom:8 }}>Period B · {labelB}</div>
                      {[
                        { label:"Total Spend",   value:fmtShort(cAgg.totalSpent),   color:"#E31E24", ref:agg.totalSpent },
                        { label:"Advance",       value:fmtShort(compTotalAdv),       color:"#d97706", ref:totalAdv },
                        { label:"Transactions",  value:cAgg.totalCnt,                color:"#4f46e5", ref:agg.totalCnt },
                        { label:"Employees",     value:cAgg.empList.length,          color:"#059669", ref:agg.empList.length },
                      ].map(k=>{
                        const d = delta(k.ref, parseFloat(k.value));
                        return (
                          <div key={k.label} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:6 }}>
                            <span style={{ fontSize:12, color:"#9ca3af" }}>{k.label}</span>
                            <div style={{ display:"flex", gap:6, alignItems:"center" }}>
                              <span style={{ fontSize:13, fontWeight:800, color:k.color }}>{k.value}</span>
                              {d!==null && (
                                <span style={{ fontSize:10, fontWeight:700, color:parseFloat(d)>0?"#DC2626":"#059669" }}>
                                  {parseFloat(d)>0?"↑":"↓"}{Math.abs(d)}%
                                </span>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Category Compare */}
                <div className="an-2col" style={{ marginBottom:16 }}>
                  <div style={card()}>
                    <div style={{ fontSize:13, fontWeight:800, color:"var(--text-primary)", marginBottom:16 }}>📊 Category Comparison</div>
                    <CategoryCompare catA={cAgg.catMap} catB={agg.catMap} labelA={labelB} labelB={labelA} />
                  </div>
                  <div style={card()}>
                    <div style={{ fontSize:13, fontWeight:800, color:"var(--text-primary)", marginBottom:16 }}>📈 Spend Trend</div>
                    <TrendChart dataA={agg.monthList} dataB={cAgg.monthList} colorA="#7C3AED" colorB="#d97706" labelA={labelA} labelB={labelB} />
                  </div>
                </div>
              </div>
            )}
            {compareMode && compLoading && (
              <div style={{ textAlign:"center", padding:"30px", color:"#9ca3af", fontSize:14, marginBottom:16 }}>
                ⏳ Loading comparison data...
              </div>
            )}

            {/* ── Smart Insights ──────────────────────────────────── */}
            {(() => {
              const insights = [];
              if (overspendTrips.length)
                insights.push({ icon:"⚠️", color:"#DC2626", bg:"#FEF2F2", text:`${overspendTrips.length} trip${overspendTrips.length>1?"s":""} overspent their advance — total overrun of ${fmtShort(overspendTrips.reduce((s,t)=>s+(t.spent-t.advance),0))}` });
              if (parseFloat(cashPct)>30)
                insights.push({ icon:"🧾", color:"#d97706", bg:"#FFFBEB", text:`${cashPct}% of spend is via cash vouchers — review recommended for compliance` });
              if (agg.empList[0] && agg.totalSpent>0)
                insights.push({ icon:"👤", color:"#7C3AED", bg:"#F5F3FF", text:`${agg.empList[0].name.split(" ")[0]} is the top spender at ${fmtShort(agg.empList[0].total)} (${(agg.empList[0].total/agg.totalSpent*100).toFixed(0)}% of total)` });
              if (agg.catSegs[0] && agg.totalSpent>0 && agg.catSegs[0].value/agg.totalSpent>0.4)
                insights.push({ icon:"📊", color:"#0891b2", bg:"#F0F9FF", text:`${agg.catSegs[0].label} dominates at ${(agg.catSegs[0].value/agg.totalSpent*100).toFixed(0)}% of all expenses` });
              return insights.length>0 ? (
                <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom:16 }}>
                  {insights.map((ins,i)=>(
                    <div key={i} style={{ display:"flex", alignItems:"flex-start", gap:10, padding:"10px 16px", borderRadius:10, background:ins.bg, border:`1px solid ${ins.color}22` }}>
                      <span style={{ fontSize:18, flexShrink:0 }}>{ins.icon}</span>
                      <span style={{ fontSize:13, color:ins.color, fontWeight:600 }}>{ins.text}</span>
                    </div>
                  ))}
                </div>
              ) : null;
            })()}

            {/* ── KPI Cards ─────────────────────────────────────── */}
            <div className="an-kpi-grid" style={{ marginBottom:16 }}>
              {[
                { label:"Total Spend",     value:fmtShort(agg.totalSpent), color:"#E31E24", bg:"#FEE2E2", icon:"💸" },
                { label:"Advance Issued",  value:fmtShort(totalAdv),        color:"#d97706", bg:"#FEF3C7", icon:"💰" },
                { label:"Utilization",     value:`${advUtil}%`,
                  color:parseFloat(advUtil)>100?"#DC2626":parseFloat(advUtil)>85?"#d97706":"#059669",
                  bg:parseFloat(advUtil)>100?"#FEE2E2":parseFloat(advUtil)>85?"#FEF3C7":"#D1FAE5",
                  icon:parseFloat(advUtil)>100?"🔴":parseFloat(advUtil)>85?"🟡":"🟢" },
                { label:"Cash Voucher %",  value:`${cashPct}%`,
                  color:parseFloat(cashPct)>50?"#DC2626":parseFloat(cashPct)>30?"#d97706":"#059669",
                  bg:parseFloat(cashPct)>50?"#FEE2E2":parseFloat(cashPct)>30?"#FEF3C7":"#D1FAE5",
                  icon:"🧾" },
                { label:"Overspend Trips", value:overspendTrips.length,
                  color:overspendTrips.length?"#DC2626":"#059669",
                  bg:overspendTrips.length?"#FEE2E2":"#D1FAE5", icon:overspendTrips.length?"⚠️":"✅" },
                { label:"Employees",       value:agg.empList.length, color:"#7C3AED", bg:"#EDE9FE", icon:"👥" },
              ].map(s=>(
                <div key={s.label} style={{ background:s.bg, borderRadius:12, padding:"13px 15px" }}>
                  <div style={{ fontSize:10, fontWeight:700, color:s.color, textTransform:"uppercase", letterSpacing:"0.06em", opacity:0.75, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.label}</div>
                  <div style={{ fontSize:17, fontWeight:800, color:s.color, marginTop:4, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.icon} {s.value}</div>
                </div>
              ))}
            </div>

            {/* ── Category + Top Spenders ───────────────────────── */}
            <div className="an-2col" style={{ marginBottom:16 }}>
              {/* Category donut */}
              <div style={card()}>
                <div style={{ fontSize:13, fontWeight:800, color:"var(--text-primary)", marginBottom:14 }}>📊 Spend by Category</div>
                <div style={{ display:"flex", gap:14, alignItems:"center" }}>
                  <DonutChart segments={agg.catSegs} size={150} thickness={32}/>
                  <div style={{ flex:1, minWidth:0 }}>
                    <DonutLegend segments={agg.catSegs} total={agg.totalSpent} compact/>
                  </div>
                </div>
              </div>
              {/* Top spenders */}
              <div style={card()}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                  <div style={{ fontSize:13, fontWeight:800, color:"var(--text-primary)" }}>🏆 Top Spenders</div>
                  <div style={{ fontSize:10, color:"#9ca3af" }}>Tap to drilldown</div>
                </div>
                <div style={{ display:"flex", flexDirection:"column", gap:5 }}>
                  {agg.empList.slice(0,8).map((emp,i)=>{
                    const pct=(emp.total/maxEmpSpend)*100;
                    const clr=COMPANY_ACCENT[emp.company]||"#6b7280";
                    const sel=drillEmp===emp.name;
                    return (
                      <div key={i} onClick={()=>setDrillEmp(sel?null:emp.name)}
                        style={{ display:"flex", alignItems:"center", gap:7, cursor:"pointer", padding:"5px 7px", borderRadius:8, background:sel?`${clr}15`:"transparent", border:`1.5px solid ${sel?clr+"50":"transparent"}`, transition:"all 0.18s" }}>
                        <span style={{ width:18,height:18,borderRadius:"50%",background:`${clr}20`,border:`2px solid ${clr}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:9,fontWeight:800,color:clr,flexShrink:0 }}>{i+1}</span>
                        <span style={{ width:80,fontSize:12,fontWeight:600,color:"var(--text-primary)",flexShrink:0,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>
                          {emp.name.split(" ").slice(0,2).join(" ")}
                        </span>
                        <div style={{ flex:1,height:18,background:"#f3f4f6",borderRadius:4,overflow:"hidden" }}>
                          <div style={{ height:"100%",width:`${pct}%`,background:`linear-gradient(90deg,${clr}99,${clr})`,borderRadius:4,minWidth:pct>0?4:0,transition:"width 0.5s" }}/>
                        </div>
                        <span style={{ width:52,fontSize:11,fontWeight:800,color:clr,textAlign:"right",flexShrink:0 }}>{fmtShort(emp.total)}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* ── Monthly Trend ──────────────────────────────────── */}
            <div style={{ ...card(), marginBottom:16 }}>
              <div style={{ fontSize:13, fontWeight:800, color:"var(--text-primary)", marginBottom:14 }}>📈 Monthly Spend Trend</div>
              <TrendChart dataA={agg.monthList} colorA="#7C3AED"/>
            </div>

            {/* ── Company Comparison (superadmin, all companies) ─── */}
            {isSuperadmin && agg.compList.length>1 && (
              <div style={{ ...card(), marginBottom:16 }}>
                <div style={{ fontSize:13, fontWeight:800, color:"var(--text-primary)", marginBottom:18 }}>🏢 Company Comparison</div>
                <div className="an-company-grid" style={{ gridTemplateColumns:`repeat(${agg.compList.length},1fr)` }}>
                  {agg.compList.map((co,i)=>{
                    const coSegs=Object.entries(co.categories).sort((a,b)=>b[1]-a[1]).map(([cat,val],j)=>({ label:cat,value:val,color:CATEGORY_COLORS[cat]||CAT_COLOR_LIST[j%CAT_COLOR_LIST.length] }));
                    return (
                      <div key={i} style={{ textAlign:"center" }}>
                        <div style={{ display:"flex",alignItems:"center",gap:6,justifyContent:"center",marginBottom:10 }}>
                          <div style={{ width:10,height:10,borderRadius:"50%",background:co.color }}/>
                          <span style={{ fontSize:13,fontWeight:800,color:co.color }}>{co.name}</span>
                          <span style={{ fontSize:11,color:"#9ca3af" }}>({agg.totalSpent>0?(co.total/agg.totalSpent*100).toFixed(0):0}%)</span>
                        </div>
                        <div style={{ display:"flex",justifyContent:"center",marginBottom:10 }}>
                          <DonutChart segments={coSegs} size={140} thickness={30}/>
                        </div>
                        <div style={{ fontSize:18,fontWeight:800,color:co.color,marginBottom:10 }}>{fmtShort(co.total)}</div>
                        <DonutLegend segments={coSegs} total={co.total} compact/>
                      </div>
                    );
                  })}
                </div>
                {/* Bar comparison */}
                <div style={{ marginTop:16,paddingTop:14,borderTop:"1px solid var(--border)" }}>
                  {agg.compList.map((co,i)=>(
                    <div key={i} style={{ display:"flex",alignItems:"center",gap:10,marginBottom:8 }}>
                      <span style={{ width:76,fontSize:12,fontWeight:600,color:co.color }}>{co.name}</span>
                      <div style={{ flex:1,height:22,background:"#f3f4f6",borderRadius:6,overflow:"hidden" }}>
                        <div style={{ height:"100%",width:`${(co.total/(agg.compList[0]?.total||1))*100}%`,background:`linear-gradient(90deg,${co.color}99,${co.color})`,borderRadius:6,display:"flex",alignItems:"center",paddingLeft:8,transition:"width 0.6s" }}>
                          <span style={{ fontSize:11,color:"white",fontWeight:700,whiteSpace:"nowrap" }}>{fmtShort(co.total)}</span>
                        </div>
                      </div>
                      <span style={{ width:36,fontSize:11,color:"#9ca3af" }}>{agg.totalSpent>0?((co.total/agg.totalSpent)*100).toFixed(0):0}%</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* ── Bill vs Cash ───────────────────────────────────── */}
            <div style={{ ...card(), marginBottom:16 }}>
              <div style={{ fontSize:13, fontWeight:800, color:"var(--text-primary)", marginBottom:14 }}>🧾 Bill vs Cash Voucher</div>
              <div className="an-2col">
                <div>
                  <div style={{ height:28,borderRadius:8,overflow:"hidden",display:"flex",marginBottom:8 }}>
                    <div style={{ width:`${agg.totalSpent>0?((agg.totalSpent-agg.cashAmt)/agg.totalSpent*100):50}%`,background:"linear-gradient(90deg,#059669aa,#059669)",display:"flex",alignItems:"center",justifyContent:"center",transition:"width 0.6s" }}>
                      {(agg.totalSpent-agg.cashAmt)/agg.totalSpent>0.1 && <span style={{ fontSize:10,color:"white",fontWeight:700 }}>{((agg.totalSpent-agg.cashAmt)/agg.totalSpent*100).toFixed(0)}%</span>}
                    </div>
                    <div style={{ flex:1,background:"linear-gradient(90deg,#d97706aa,#d97706)",display:"flex",alignItems:"center",justifyContent:"center" }}>
                      {agg.cashAmt/agg.totalSpent>0.1 && <span style={{ fontSize:10,color:"white",fontWeight:700 }}>{cashPct}%</span>}
                    </div>
                  </div>
                  <div style={{ display:"flex",gap:14,fontSize:12 }}>
                    <div style={{ display:"flex",gap:5,alignItems:"center" }}><div style={{ width:10,height:10,borderRadius:2,background:"#059669" }}/> Bills</div>
                    <div style={{ display:"flex",gap:5,alignItems:"center" }}><div style={{ width:10,height:10,borderRadius:2,background:"#d97706" }}/> Cash Vouchers</div>
                  </div>
                </div>
                <div style={{ display:"flex",gap:10 }}>
                  <div style={{ flex:1,background:"#D1FAE5",borderRadius:10,padding:"10px 12px" }}>
                    <div style={{ fontSize:10,fontWeight:700,color:"#059669",textTransform:"uppercase" }}>Bills</div>
                    <div style={{ fontSize:16,fontWeight:800,color:"#059669",marginTop:3 }}>{fmtShort(agg.totalSpent-agg.cashAmt)}</div>
                    <div style={{ fontSize:11,color:"#9ca3af",marginTop:2 }}>{agg.totalCnt-agg.cashCnt} items</div>
                  </div>
                  <div style={{ flex:1,background:"#FEF3C7",borderRadius:10,padding:"10px 12px" }}>
                    <div style={{ fontSize:10,fontWeight:700,color:"#d97706",textTransform:"uppercase" }}>Cash</div>
                    <div style={{ fontSize:16,fontWeight:800,color:"#d97706",marginTop:3 }}>{fmtShort(agg.cashAmt)}</div>
                    <div style={{ fontSize:11,color:"#9ca3af",marginTop:2 }}>{agg.cashCnt} items</div>
                  </div>
                </div>
              </div>
            </div>

            {/* ── Food Subcategory Breakdown ─────────────────────── */}
            {(() => {
              const foodTotal = Object.values(agg.foodSub).reduce((s,v)=>s+v, 0);
              if (!foodTotal) return null;
              const foodSegs = FOOD_SUB_ORDER
                .filter(k => agg.foodSub[k] > 0)
                .map(k => ({ label: k, value: agg.foodSub[k], color: FOOD_SUB_COLORS[k] }));
              // Bar chart — employees sorted most→least total food spend
              const maxFoodEmp = agg.foodEmpList[0]
                ? FOOD_SUB_ORDER.reduce((s,k)=>s+(agg.foodEmpList[0].foodSub[k]||0), 0)
                : 1;
              return (
                <div style={{ ...card(), marginBottom:16 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:18 }}>
                    <span style={{ fontSize:20 }}>🍽️</span>
                    <div>
                      <div style={{ fontSize:13, fontWeight:800, color:"var(--text-primary)" }}>Food Expense Breakdown</div>
                      <div style={{ fontSize:11, color:"#9ca3af" }}>Split by Breakfast, Lunch & Dinner · Total: {fmtShort(foodTotal)}</div>
                    </div>
                  </div>

                  <div className="an-2col">
                    {/* Left: Donut + legend */}
                    <div>
                      <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:12 }}>Category Split</div>
                      <div style={{ display:"flex", gap:14, alignItems:"center" }}>
                        <DonutChart segments={foodSegs} size={140} thickness={30}/>
                        <div style={{ flex:1 }}>
                          {foodSegs.map(s=>(
                            <div key={s.label} style={{ display:"flex", alignItems:"center", gap:8, marginBottom:10 }}>
                              <span style={{ fontSize:18 }}>{FOOD_SUB_ICONS[s.label]||"🍽️"}</span>
                              <div style={{ flex:1 }}>
                                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                                  <span style={{ fontSize:12, fontWeight:700, color:s.color }}>{s.label}</span>
                                  <span style={{ fontSize:12, fontWeight:800, color:s.color }}>{fmtShort(s.value)}</span>
                                </div>
                                <div style={{ height:7, background:"#f3f4f6", borderRadius:4, overflow:"hidden" }}>
                                  <div style={{ height:"100%", width:`${(s.value/foodTotal*100).toFixed(0)}%`, background:s.color, borderRadius:4, transition:"width 0.5s" }}/>
                                </div>
                                <div style={{ fontSize:10, color:"#9ca3af", marginTop:2 }}>{(s.value/foodTotal*100).toFixed(1)}% of food</div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>

                    {/* Right: Employee stacked bars, most→least */}
                    <div>
                      <div style={{ fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:12 }}>By Employee (Most → Least)</div>
                      {/* Legend */}
                      <div style={{ display:"flex", gap:12, marginBottom:12 }}>
                        {FOOD_SUB_ORDER.map(k=>(
                          <div key={k} style={{ display:"flex", alignItems:"center", gap:5 }}>
                            <span style={{ fontSize:13 }}>{FOOD_SUB_ICONS[k]}</span>
                            <div style={{ width:10, height:10, borderRadius:2, background:FOOD_SUB_COLORS[k] }}/>
                            <span style={{ fontSize:11, color:"#d1d5db" }}>{k}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
                        {agg.foodEmpList.slice(0,10).map((emp,i)=>{
                          const empFoodTotal = FOOD_SUB_ORDER.reduce((s,k)=>s+(emp.foodSub[k]||0), 0);
                          const pct = (empFoodTotal/maxFoodEmp*100).toFixed(0);
                          const co = COMPANY_ACCENT[emp.company]||"#6b7280";
                          return (
                            <div key={emp.name}>
                              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:3 }}>
                                <span style={{ fontSize:11, fontWeight:700, color:"var(--text-primary)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"65%" }}>
                                  {i===0?"🥇":i===1?"🥈":i===2?"🥉":`${i+1}.`} {emp.name.split(" ").slice(0,2).join(" ")}
                                </span>
                                <span style={{ fontSize:11, fontWeight:800, color:co, flexShrink:0 }}>{fmtShort(empFoodTotal)}</span>
                              </div>
                              {/* Stacked bar: BF | Lunch | Dinner */}
                              <div style={{ height:16, background:"#f3f4f6", borderRadius:6, overflow:"hidden", display:"flex" }}>
                                {FOOD_SUB_ORDER.map(k=>{
                                  const segAmt = emp.foodSub[k]||0;
                                  if (!segAmt) return null;
                                  const segPct = (segAmt/maxFoodEmp*100).toFixed(1);
                                  return (
                                    <div key={k} title={`${k}: ₹${segAmt.toLocaleString("en-IN")}`}
                                      style={{ height:"100%", width:`${segPct}%`, background:FOOD_SUB_COLORS[k], minWidth:segAmt>0?3:0, borderRight:"1px solid rgba(255,255,255,0.3)", transition:"width 0.5s" }}/>
                                  );
                                })}
                              </div>
                              <div style={{ display:"flex", gap:8, marginTop:2, fontSize:10, color:"#9ca3af" }}>
                                {FOOD_SUB_ORDER.map(k => emp.foodSub[k] > 0 ? (
                                  <span key={k} style={{ color:FOOD_SUB_COLORS[k] }}>{FOOD_SUB_ICONS[k]} {fmtShort(emp.foodSub[k])}</span>
                                ) : null)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}

            {/* ── Trip Efficiency ────────────────────────────────── */}
            {agg.tripList.length>0 && (
              <div style={{ ...card(), marginBottom:16 }}>
                <div style={{ fontSize:13,fontWeight:800,color:"var(--text-primary)",marginBottom:4 }}>✈️ Advance vs Actual — Trips</div>
                <div style={{ fontSize:11,color:"#9ca3af",marginBottom:14 }}>🟢 Under advance · 🔴 Overspent</div>
                <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
                  {agg.tripList.slice(0,10).sort((a,b)=>(b.spent-b.advance)-(a.spent-a.advance)).map((t,i)=>{
                    const over=t.spent>t.advance;
                    const pct=t.advance>0?Math.min((t.spent/t.advance)*100,150):100;
                    const clr=over?"#DC2626":"#059669";
                    const bal=t.advance-t.spent;
                    return (
                      <div key={i} style={{ padding:"10px 12px",borderRadius:10,background:over?"#FEF2F2":"#F0FDF4",border:`1px solid ${clr}20` }}>
                        <div style={{ display:"flex",alignItems:"flex-start",gap:8,marginBottom:7 }}>
                          <span style={{ fontSize:14,flexShrink:0 }}>{over?"🔴":"🟢"}</span>
                          <div style={{ flex:1,minWidth:0 }}>
                            <div style={{ fontWeight:700,fontSize:12,color:"var(--text-primary)",overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{t.title}</div>
                            <div style={{ fontSize:11,color:"#9ca3af" }}>{t.employee}{t.dest?` · 📍 ${t.dest}`:""}</div>
                          </div>
                          <div style={{ fontSize:12,fontWeight:800,color:clr,flexShrink:0 }}>
                            {over?`+${fmtShort(Math.abs(bal))} over`:`${fmtShort(bal)} left`}
                          </div>
                        </div>
                        <div style={{ height:8,background:"#e5e7eb",borderRadius:4,overflow:"hidden",marginBottom:4 }}>
                          <div style={{ height:"100%",width:`${Math.min(pct,100)}%`,background:`linear-gradient(90deg,${clr}99,${clr})`,borderRadius:4,transition:"width 0.6s" }}/>
                        </div>
                        <div style={{ display:"flex",justifyContent:"space-between",fontSize:10,color:"#9ca3af" }}>
                          <span>Advance: {fmtShort(t.advance)}</span>
                          <span>Spent: <strong style={{ color:clr }}>{fmtShort(t.spent)}</strong></span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* ── Employee Drilldown ─────────────────────────────── */}
            {drillData && (
              <div style={{ ...card({ border:`2px solid ${COMPANY_ACCENT[drillData.company]||"#7C3AED"}40`, background:`${COMPANY_ACCENT[drillData.company]||"#7C3AED"}06` }) }}>
                <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:18 }}>
                  <div style={{ width:38,height:38,borderRadius:"50%",background:`linear-gradient(135deg,${COMPANY_ACCENT[drillData.company]||"#7C3AED"},${COMPANY_ACCENT[drillData.company]||"#7C3AED"}88)`,display:"flex",alignItems:"center",justifyContent:"center",color:"white",fontWeight:800,fontSize:17 }}>
                    {drillData.name[0].toUpperCase()}
                  </div>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:15,fontWeight:800,color:"var(--text-primary)" }}>{drillData.name}</div>
                    <div style={{ fontSize:12,color:"#9ca3af" }}>
                      {drillData.dept&&`${drillData.dept} · `}
                      <span style={{ color:COMPANY_ACCENT[drillData.company]||"#7C3AED",fontWeight:700 }}>{COMPANY_LABELS[drillData.company]||drillData.company}</span>
                      {" · "}{fmtShort(drillData.total)} total
                      {agg.totalSpent>0 && ` · ${(drillData.total/agg.totalSpent*100).toFixed(1)}% of company`}
                    </div>
                  </div>
                  <button onClick={()=>setDrillEmp(null)} style={{ background:"none",border:"none",fontSize:20,cursor:"pointer",color:"#9ca3af" }}>✕</button>
                </div>
                <div className="an-drill-cols">
                  {/* Category breakdown */}
                  <div>
                    <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",marginBottom:12 }}>Spend Breakdown</div>
                    <div style={{ display:"flex",gap:14,alignItems:"center" }}>
                      <DonutChart segments={drillSegs} size={130} thickness={28}/>
                      <div style={{ flex:1,minWidth:0 }}><DonutLegend segments={drillSegs} total={drillData.total} compact/></div>
                    </div>
                  </div>
                  {/* Trips */}
                  <div>
                    <div style={{ fontSize:11,fontWeight:700,color:"#9ca3af",textTransform:"uppercase",marginBottom:12 }}>Trips</div>
                    <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
                      {Object.values(drillData.trips).map((tr,i)=>{
                        const bal=tr.advance-tr.spent; const over=tr.spent>tr.advance;
                        return (
                          <div key={i} style={{ padding:"9px 11px",borderRadius:8,background:"var(--bg-surface)",border:"1px solid var(--border)" }}>
                            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"flex-start",marginBottom:4 }}>
                              <div>
                                <div style={{ fontSize:12,fontWeight:700,color:"var(--text-primary)" }}>{tr.title}</div>
                                {tr.dest&&<div style={{ fontSize:11,color:"#9ca3af" }}>📍 {tr.dest}</div>}
                              </div>
                              <span className={`badge badge-${tr.status}`} style={{ fontSize:10 }}>{tr.status}</span>
                            </div>
                            <div style={{ display:"flex",gap:10,fontSize:11 }}>
                              <span style={{ color:"#d97706" }}>💰 {fmtShort(tr.advance)}</span>
                              <span style={{ color:"#E31E24" }}>💸 {fmtShort(tr.spent)}</span>
                              <span style={{ color:over?"#DC2626":"#059669",fontWeight:700 }}>
                                {over?`⚠️ +${fmtShort(Math.abs(bal))}`:`✅ ${fmtShort(bal)} left`}
                              </span>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* ── Trip Category Employee Ranking ─────────────────── */}
            {tripCategory && empCategoryData.length > 0 && (
              <div style={{ ...card({ marginBottom:16, border:"2px solid #7C3AED" }) }}>
                <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
                  <span style={{ fontSize:18 }}>🏆</span>
                  <div>
                    <div style={{ fontSize:14, fontWeight:800, color:"var(--text-primary)" }}>Employee Ranking — {tripCategory}</div>
                    <div style={{ fontSize:12, color:"#9ca3af" }}>Total spent per employee on {tripCategory} trips</div>
                  </div>
                </div>
                {empCategoryData.map((emp, i) => {
                  const maxVal = empCategoryData[0]?.total || 1;
                  const pct = (emp.total / maxVal * 100).toFixed(0);
                  const colors = ["#7C3AED","#2563EB","#059669","#D97706","#DC2626","#0891B2"];
                  const col = colors[i % colors.length];
                  return (
                    <div key={emp.name} style={{ marginBottom:12 }}>
                      <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4 }}>
                        <span style={{ fontSize:13, fontWeight:700, color:"var(--text-primary)" }}>
                          {i===0?"🥇":i===1?"🥈":i===2?"🥉":"  "} {emp.name}
                        </span>
                        <span style={{ fontSize:13, fontWeight:700, color:col }}>₹{emp.total.toLocaleString("en-IN")}</span>
                      </div>
                      <div style={{ height:8, borderRadius:4, background:"var(--border)", overflow:"hidden" }}>
                        <div style={{ height:"100%", width:`${pct}%`, background:col, borderRadius:4, transition:"width 0.4s" }}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Employee Compare Section ───────────────────────── */}
            <div style={{ ...card({ marginBottom:16 }) }}>
              <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: empCompareOpen ? 18 : 0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:10 }}>
                  <span style={{ fontSize:18 }}>👥</span>
                  <span style={{ fontSize:14, fontWeight:800, color:"var(--text-primary)" }}>Employee Compare</span>
                </div>
                <button onClick={() => setEmpCompareOpen(o=>!o)}
                  style={{ padding:"6px 14px", borderRadius:8, border:"1.5px solid var(--border)", background: empCompareOpen ? "#7C3AED" : "var(--bg-card)", color: empCompareOpen ? "white" : "var(--text-muted)", fontWeight:700, fontSize:12, cursor:"pointer" }}>
                  {empCompareOpen ? "▲ Close" : "▼ Open"}
                </button>
              </div>

              {empCompareOpen && agg && (
                <div>
                  {/* Mode tabs */}
                  <div style={{ display:"flex", gap:8, marginBottom:16, borderBottom:"2px solid var(--border)", paddingBottom:12 }}>
                    {[["single","One Employee"],["all","All Employees"]].map(([key,label])=>(
                      <button key={key} onClick={()=>{ setEmpCompareMode(key); setEmpCompareSubMode("month"); setEmpCompareName(""); }}
                        style={{ padding:"7px 16px", borderRadius:8, border:"none", fontWeight:700, fontSize:13, cursor:"pointer",
                          background: empCompareMode===key ? "#7C3AED" : "rgba(255,255,255,0.08)",
                          color: empCompareMode===key ? "white" : "#d1d5db" }}>
                        {label}
                      </button>
                    ))}
                    <div style={{ flex:1 }}/>
                    {[["month","Month-on-Month"],["trip","Trip-on-Trip"]].map(([key,label])=>(
                      <button key={key} onClick={()=>setEmpCompareSubMode(key)}
                        style={{ padding:"7px 14px", borderRadius:8, border:"none", fontWeight:700, fontSize:12, cursor:"pointer",
                          background: empCompareSubMode===key ? "#E31E24" : "rgba(255,255,255,0.08)",
                          color: empCompareSubMode===key ? "white" : "#d1d5db" }}>
                        {label}
                      </button>
                    ))}
                  </div>

                  {/* ─── Single Employee ─── */}
                  {empCompareMode === "single" && (
                    <div>
                      <div style={{ marginBottom:16 }}>
                        <label style={{ fontSize:12, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:6, display:"block" }}>Select Employee</label>
                        <select value={empCompareName} onChange={e=>setEmpCompareName(e.target.value)}
                          className={selClass}
                          style={{ padding:"8px 12px", borderRadius:8, border:"1.5px solid rgba(255,255,255,0.2)", fontSize:13, background:"#1a1b2e", color:"#f9fafb", width:"100%", maxWidth:320, colorScheme:"dark" }}>
                          <option value="">— Choose an employee —</option>
                          {agg.empList.map(emp=>(
                            <option key={emp.name} value={emp.name}>{emp.name} ({fmtShort(emp.total)})</option>
                          ))}
                        </select>
                      </div>

                      {empCompareName && empCompareSubMode === "month" && (
                        <div>
                          <div style={{ fontSize:12, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:12 }}>
                            {empCompareName} — Monthly Spend Trend
                          </div>
                          {singleEmpMonths.length < 2 ? (
                            <div style={{ textAlign:"center", padding:30, color:"#9ca3af", fontSize:13 }}>Not enough data for a trend (need at least 2 months)</div>
                          ) : (
                            <TrendChart dataA={singleEmpMonths} colorA="#7C3AED" labelA={empCompareName} />
                          )}
                        </div>
                      )}

                      {empCompareName && empCompareSubMode === "trip" && (() => {
                        const empData = agg.empList.find(e=>e.name===empCompareName);
                        const trips = empData ? Object.values(empData.trips).sort((a,b)=>b.spent-a.spent) : [];
                        if (!trips.length) return <div style={{ textAlign:"center", padding:30, color:"#9ca3af" }}>No trips found</div>;
                        const maxSpent = Math.max(...trips.map(t=>t.spent), 1);
                        return (
                          <div>
                            <div style={{ fontSize:12, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:14 }}>
                              {empCompareName} — Trip Spend Comparison
                            </div>
                            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                              {trips.map((t,i)=>(
                                <div key={i}>
                                  <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4, fontSize:12 }}>
                                    <span style={{ fontWeight:600, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", maxWidth:"70%" }}>{t.title}</span>
                                    <span style={{ fontWeight:800, color:"#E31E24", flexShrink:0 }}>{fmtShort(t.spent)}</span>
                                  </div>
                                  <div style={{ height:16, background:"#f3f4f6", borderRadius:6, overflow:"hidden" }}>
                                    <div style={{ height:"100%", width:`${(t.spent/maxSpent)*100}%`, background:`linear-gradient(90deg,#E31E2499,#E31E24)`, borderRadius:6, minWidth:t.spent>0?4:0, transition:"width 0.5s" }}/>
                                  </div>
                                  <div style={{ fontSize:10, color:"#9ca3af", marginTop:3 }}>
                                    Advance: {fmtShort(t.advance)} · {t.dest||""}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  )}

                  {/* ─── All Employees ─── */}
                  {empCompareMode === "all" && (
                    <div>
                      {empCompareSubMode === "trip" && (() => {
                        const topTrips = agg.tripList.slice(0,15).sort((a,b)=>b.spent-a.spent);
                        if (!topTrips.length) return <div style={{ textAlign:"center", padding:30, color:"#9ca3af" }}>No trip data</div>;
                        const maxSpent = Math.max(...topTrips.map(t=>t.spent), 1);
                        return (
                          <div>
                            <div style={{ fontSize:12, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:14 }}>All Employees — Top Trips by Spend</div>
                            <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                              {topTrips.map((t,i)=>{
                                const color = COMPANY_ACCENT[t.company] || "#7C3AED";
                                return (
                                  <div key={i}>
                                    <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4, fontSize:12, gap:8 }}>
                                      <div style={{ flex:1, minWidth:0 }}>
                                        <span style={{ fontWeight:700, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", display:"block" }}>{t.title}</span>
                                        <span style={{ fontSize:11, color:"#9ca3af" }}>{t.employee}</span>
                                      </div>
                                      <span style={{ fontWeight:800, color, flexShrink:0 }}>{fmtShort(t.spent)}</span>
                                    </div>
                                    <div style={{ height:14, background:"#f3f4f6", borderRadius:6, overflow:"hidden" }}>
                                      <div style={{ height:"100%", width:`${(t.spent/maxSpent)*100}%`, background:`linear-gradient(90deg,${color}88,${color})`, borderRadius:6, transition:"width 0.5s" }}/>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      })()}

                      {empCompareSubMode === "month" && (() => {
                        if (!allEmpMonths.length) return <div style={{ textAlign:"center", padding:30, color:"#9ca3af" }}>No data</div>;
                        // Collect all months across all employees
                        const allMonthKeys = [...new Set(allEmpMonths.flatMap(e=>e.months.map(m=>m.month)))].sort();
                        if (allMonthKeys.length < 2) return <div style={{ textAlign:"center", padding:30, color:"#9ca3af", fontSize:13 }}>Not enough monthly data</div>;

                        const W=540, H=180;
                        const PAD={t:20,r:16,b:34,l:52};
                        const iW=W-PAD.l-PAD.r, iH=H-PAD.t-PAD.b;
                        const maxV = Math.max(...allEmpMonths.flatMap(e=>e.months.map(m=>m.total)), 1);

                        const svgPt = (months, monthKey) => {
                          const idx = allMonthKeys.indexOf(monthKey);
                          const val = months.find(m=>m.month===monthKey)?.total || 0;
                          return {
                            x: PAD.l + (allMonthKeys.length===1 ? iW/2 : (idx/(allMonthKeys.length-1))*iW),
                            y: PAD.t + iH - (val/maxV)*iH,
                          };
                        };

                        const gridY = [0,0.25,0.5,0.75,1].map(f=>({ y:PAD.t+iH-f*iH, label:f===0?"0":fmtShort(maxV*f) }));

                        return (
                          <div>
                            <div style={{ fontSize:12, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:12 }}>
                              Top {allEmpMonths.length} Employees — Month-on-Month
                            </div>
                            {/* Legend */}
                            <div style={{ display:"flex", gap:14, flexWrap:"wrap", marginBottom:12, fontSize:12 }}>
                              {allEmpMonths.map(e=>(
                                <div key={e.name} style={{ display:"flex", alignItems:"center", gap:6 }}>
                                  <div style={{ width:18, height:3, background:e.color, borderRadius:2 }}/>
                                  <span style={{ color:"#d1d5db" }}>{e.name}</span>
                                </div>
                              ))}
                            </div>
                            <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", overflow:"visible" }}>
                              {/* Grid */}
                              {gridY.map((g,i)=>(
                                <g key={i}>
                                  <line x1={PAD.l} y1={g.y} x2={W-PAD.r} y2={g.y} stroke="rgba(255,255,255,0.08)" strokeWidth={1}/>
                                  <text x={PAD.l-5} y={g.y+3.5} textAnchor="end" fontSize={9} fill="#d1d5db">{g.label}</text>
                                </g>
                              ))}
                              {/* X-axis labels */}
                              {allMonthKeys.map((mk,i)=>{
                                const x = PAD.l + (allMonthKeys.length===1 ? iW/2 : (i/(allMonthKeys.length-1))*iW);
                                return <text key={mk} x={x} y={H-5} textAnchor="middle" fontSize={9} fill="#e2e8f0">{MONTHS_LABEL[parseInt(mk.substring(5,7))-1]}</text>;
                              })}
                              {/* Lines per employee */}
                              {allEmpMonths.map((emp, ei) => {
                                const pts = allMonthKeys.map(mk => svgPt(emp.months, mk));
                                const d = pts.map((p,i)=>`${i===0?"M":"L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
                                return (
                                  <g key={emp.name}>
                                    <path d={d} fill="none" stroke={emp.color} strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" strokeDasharray={ei>0?"6,3":""} opacity={0.85}/>
                                    {pts.map((p,i)=>(
                                      <circle key={i} cx={p.x} cy={p.y} r={3} fill={emp.color} stroke="white" strokeWidth={1.5}/>
                                    ))}
                                  </g>
                                );
                              })}
                            </svg>
                          </div>
                        );
                      })()}
                    </div>
                  )}
                </div>
              )}
            </div>

          </>
        )}

        {/* ════════════════════════════════════════════════════════
             INSIGHT TABS — shared loading + three tab panels
            ════════════════════════════════════════════════════════ */}

        {/* Shared loading spinner */}
        {activeTab !== "spend" && insightLoading && (
          <div style={{ display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", minHeight:300, gap:16 }}>
            <div style={{ width:40, height:40, border:"4px solid #7C3AED33", borderTopColor:"#7C3AED", borderRadius:"50%", animation:"spin 0.9s linear infinite" }}/>
            <div style={{ color:"#9ca3af", fontSize:13 }}>Loading insights…</div>
          </div>
        )}

        {/* ══════════════════════ BUDGET VS ACTUAL TAB ═══════════════ */}
        {activeTab === "budget" && (
          <div>
            {budgetLoading && (
              <div style={{ textAlign:"center", padding:"60px 20px" }}>
                <div style={{ fontSize:36, marginBottom:12 }}>⏳</div>
                <div style={{ color:"#9ca3af", fontSize:14 }}>Loading budget data…</div>
              </div>
            )}
            {!budgetLoading && !budgetData && (
              <div style={{ textAlign:"center", padding:"60px 20px" }}>
                <div style={{ fontSize:48, marginBottom:12 }}>🎯</div>
                <div style={{ color:"#d1d5db", fontSize:16, fontWeight:700 }}>No budget data yet</div>
                <div style={{ color:"#9ca3af", fontSize:13, marginTop:6 }}>Employees need to submit expense projections with their advance requests.</div>
                <button onClick={() => fetchBudget(company, year, month, customFrom, customTo)}
                  style={{ marginTop:16, padding:"9px 20px", borderRadius:8, border:"2px solid #7C3AED", background:"#7C3AED", color:"white", fontWeight:700, fontSize:13, cursor:"pointer" }}>
                  🔄 Load Budget Data
                </button>
              </div>
            )}
            {!budgetLoading && budgetData && (() => {
              const catRows = (budgetData.by_category || []).filter(r => parseFloat(r.total_projected || 0) > 0);
              const overageTrips = (budgetData.overage_trips || []).filter(t => !t.overage_approved);
              const ICONS = { Travel:"🚂", Hotel:"🏨", Food:"🍽️", Fuel:"⛽", Courier:"📦", Stationeries:"📝", Miscellaneous:"📌" };
              const COLORS = { Travel:"#4f46e5", Hotel:"#0891b2", Food:"#059669", Fuel:"#d97706", Courier:"#7C3AED", Stationeries:"#E31E24", Prints:"#ec4899", Miscellaneous:"#6b7280" };

              const totalProjected = catRows.reduce((s, r) => s + parseFloat(r.total_projected || 0), 0);
              const totalActual    = catRows.reduce((s, r) => s + parseFloat(r.total_actual    || 0), 0);
              const maxVal         = Math.max(...catRows.map(r => Math.max(parseFloat(r.total_projected || 0), parseFloat(r.total_actual || 0))), 1);

              return (
                <>
                  {/* KPI summary */}
                  <div className="an-kpi-grid" style={{ marginBottom:16 }}>
                    {[
                      { label:"Total Projected", value:fmtShort(totalProjected), color:"#059669", bg:"#D1FAE5", icon:"🎯" },
                      { label:"Total Actual",    value:fmtShort(totalActual),    color: totalActual > totalProjected ? "#DC2626" : "#059669", bg: totalActual > totalProjected ? "#FEE2E2" : "#D1FAE5", icon:"💸" },
                      { label:"Variance",        value:`${totalProjected > 0 ? ((totalActual - totalProjected) / totalProjected * 100).toFixed(1) : 0}%`,
                        color: totalActual > totalProjected ? "#DC2626" : "#059669",
                        bg:    totalActual > totalProjected ? "#FEE2E2" : "#D1FAE5", icon: totalActual > totalProjected ? "🔴" : "🟢" },
                      { label:"Overage Trips",   value: overageTrips.length,
                        color: overageTrips.length > 0 ? "#DC2626" : "#059669",
                        bg:    overageTrips.length > 0 ? "#FEE2E2" : "#D1FAE5", icon: overageTrips.length > 0 ? "⚠️" : "✅" },
                    ].map(s => (
                      <div key={s.label} style={{ background:s.bg, borderRadius:12, padding:"13px 15px" }}>
                        <div style={{ fontSize:10, fontWeight:700, color:s.color, textTransform:"uppercase", letterSpacing:"0.06em", opacity:0.75, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{s.label}</div>
                        <div style={{ fontSize:17, fontWeight:800, color:s.color, marginTop:4 }}>{s.icon} {s.value}</div>
                      </div>
                    ))}
                  </div>

                  {/* Category comparison bars */}
                  {catRows.length > 0 && (
                    <div style={{ ...card(), marginBottom:16 }}>
                      <div style={{ fontSize:13, fontWeight:800, color:"var(--text-primary)", marginBottom:18 }}>📊 Expected vs Actual by Category</div>
                      <div style={{ display:"flex", gap:16, marginBottom:14, fontSize:12 }}>
                        {[{c:"#059669",l:"Projected"},{c:"#DC2626",l:"Actual"}].map((x,i) => (
                          <div key={i} style={{ display:"flex", alignItems:"center", gap:6 }}>
                            <div style={{ width:18, height:4, borderRadius:2, background:x.c }}/>
                            <span style={{ color:"#d1d5db", fontWeight:600 }}>{x.l}</span>
                          </div>
                        ))}
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
                        {catRows.sort((a,b) => parseFloat(b.total_projected||0) - parseFloat(a.total_projected||0)).map(row => {
                          const proj   = parseFloat(row.total_projected || 0);
                          const actual = parseFloat(row.total_actual    || 0);
                          const over   = actual > proj;
                          const pct    = proj > 0 ? ((actual - proj) / proj * 100).toFixed(0) : null;
                          const clr    = COLORS[row.category] || "#6b7280";
                          return (
                            <div key={row.category}>
                              <div style={{ display:"flex", justifyContent:"space-between", marginBottom:6, fontSize:13 }}>
                                <span style={{ fontWeight:700, color:"#f9fafb" }}>{ICONS[row.category]||"📌"} {row.category}</span>
                                {pct !== null && <span style={{ color: over?"#DC2626":"#059669", fontWeight:700, fontSize:12 }}>{over?"↑":"↓"}{Math.abs(pct)}%</span>}
                              </div>
                              {/* Projected bar */}
                              <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                                <div style={{ width:70, fontSize:11, color:"#059669", fontWeight:700, textAlign:"right", flexShrink:0 }}>{fmtShort(proj)}</div>
                                <div style={{ flex:1, height:14, background:"rgba(255,255,255,0.1)", borderRadius:4, overflow:"hidden" }}>
                                  <div style={{ height:"100%", width:`${(proj/maxVal)*100}%`, background:"linear-gradient(90deg,#059669aa,#059669)", borderRadius:4, minWidth:proj>0?4:0 }}/>
                                </div>
                              </div>
                              {/* Actual bar */}
                              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                <div style={{ width:70, fontSize:11, color: over?"#DC2626":"#059669", fontWeight:700, textAlign:"right", flexShrink:0 }}>{fmtShort(actual)}</div>
                                <div style={{ flex:1, height:14, background:"rgba(255,255,255,0.1)", borderRadius:4, overflow:"hidden", position:"relative" }}>
                                  <div style={{ height:"100%", width:`${(actual/maxVal)*100}%`, background:`linear-gradient(90deg,${over?"#DC2626":"#059669"}aa,${over?"#DC2626":"#059669"})`, borderRadius:4, minWidth:actual>0?4:0 }}/>
                                  {over && proj > 0 && (
                                    <div style={{ position:"absolute", left:`${(proj/maxVal)*100}%`, top:0, bottom:0, width:2, background:"white", opacity:0.6 }}/>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Overage trips requiring approval */}
                  {overageTrips.length > 0 && (
                    <div style={{ ...card(), marginBottom:16, border:"2px solid #FCA5A5" }}>
                      <div style={{ fontSize:13, fontWeight:800, color:"#DC2626", marginBottom:16 }}>
                        ⚠️ Trips Requiring Overage Approval
                        <span style={{ marginLeft:8, background:"#DC2626", color:"white", borderRadius:20, padding:"2px 10px", fontSize:12 }}>{overageTrips.length}</span>
                      </div>
                      <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                        {overageTrips.map(trip => {
                          const proj   = parseFloat(trip.projected || 0);
                          const actual = parseFloat(trip.actual || 0);
                          const overPct = proj > 0 ? ((actual - proj) / proj * 100).toFixed(0) : 0;
                          const isNoteOpen = budgetOverageNoteMode === trip.id;
                          const isProcessing = budgetOverageProcessing === trip.id;
                          return (
                            <div key={trip.id} style={{ background:"rgba(255,255,255,0.05)", borderRadius:10, padding:"14px 16px", border:"1px solid rgba(220,38,38,0.3)" }}>
                              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:8, marginBottom:10 }}>
                                <div>
                                  <div style={{ fontSize:14, fontWeight:700, color:"#f9fafb" }}>{trip.title}</div>
                                  <div style={{ fontSize:12, color:"#9ca3af", marginTop:2 }}>👤 {trip.employee_name}</div>
                                </div>
                                <span style={{ background:"#FEE2E2", color:"#DC2626", borderRadius:8, padding:"3px 10px", fontSize:12, fontWeight:700, flexShrink:0 }}>+{overPct}% over budget</span>
                              </div>
                              <div style={{ display:"flex", gap:20, fontSize:13, marginBottom:12 }}>
                                <div><span style={{ color:"#9ca3af" }}>Projected: </span><span style={{ fontWeight:700, color:"#059669" }}>₹{proj.toLocaleString("en-IN")}</span></div>
                                <div><span style={{ color:"#9ca3af" }}>Actual: </span><span style={{ fontWeight:700, color:"#DC2626" }}>₹{actual.toLocaleString("en-IN")}</span></div>
                                <div><span style={{ color:"#9ca3af" }}>Overrun: </span><span style={{ fontWeight:700, color:"#DC2626" }}>₹{(actual-proj).toLocaleString("en-IN")}</span></div>
                              </div>
                              {isNoteOpen && (
                                <textarea value={budgetOverageNote} onChange={e => setBudgetOverageNote(e.target.value)}
                                  placeholder="Optional approval notes (e.g. extra cost approved due to unforeseen circumstances)"
                                  rows={2} style={{ width:"100%", padding:"8px 12px", borderRadius:8, border:"1px solid rgba(255,255,255,0.2)", background:"rgba(0,0,0,0.2)", color:"#f9fafb", fontSize:13, resize:"vertical", fontFamily:"inherit", boxSizing:"border-box", marginBottom:10 }}/>
                              )}
                              <div style={{ display:"flex", gap:8, flexWrap:"wrap" }}>
                                {!isNoteOpen ? (
                                  <button onClick={() => { setBudgetOverageNoteMode(trip.id); setBudgetOverageNote(""); }}
                                    style={{ background:"#059669", color:"white", border:"none", borderRadius:8, padding:"8px 18px", fontSize:13, fontWeight:700, cursor:"pointer" }}>
                                    ✅ Approve Overage
                                  </button>
                                ) : (
                                  <>
                                    <button disabled={isProcessing} onClick={async () => {
                                      setBudgetOverageProcessing(trip.id);
                                      try {
                                        await API.post(`/trips/${trip.id}/approve-overage`, { notes: budgetOverageNote });
                                        toast.success(`Overage approved for "${trip.title}"`);
                                        setBudgetData(prev => ({
                                          ...prev,
                                          overage_trips: (prev.overage_trips || []).map(t => t.id === trip.id ? { ...t, overage_approved: true } : t),
                                        }));
                                        setBudgetOverageNoteMode(null);
                                      } catch { toast.error("Failed to approve"); }
                                      finally { setBudgetOverageProcessing(null); }
                                    }}
                                      style={{ background: isProcessing?"#9ca3af":"#059669", color:"white", border:"none", borderRadius:8, padding:"8px 18px", fontSize:13, fontWeight:700, cursor: isProcessing?"not-allowed":"pointer" }}>
                                      {isProcessing ? "Saving..." : "Confirm"}
                                    </button>
                                    <button onClick={() => setBudgetOverageNoteMode(null)}
                                      style={{ background:"none", color:"#9ca3af", border:"1px solid rgba(255,255,255,0.2)", borderRadius:8, padding:"8px 14px", fontSize:13, cursor:"pointer" }}>
                                      Cancel
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {catRows.length === 0 && overageTrips.length === 0 && (
                    <div style={{ textAlign:"center", padding:"40px", color:"#9ca3af" }}>
                      <div style={{ fontSize:40, marginBottom:12 }}>🎯</div>
                      <div style={{ fontSize:15, fontWeight:600 }}>No projection data for this period</div>
                      <div style={{ fontSize:13, marginTop:6 }}>Budget vs Actual comparison will appear once employees submit expense projections with their advance requests.</div>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        )}

        {/* ── COMPLIANCE TAB ───────────────────────────────────────── */}
        {activeTab === "compliance" && !insightLoading && (
          <>
            {/* Employee Integrity Ranking */}
            <div style={{ background:"var(--card)", borderRadius:16, padding:24, marginBottom:20, border:"1px solid var(--border)" }}>
              <div style={{ fontSize:15, fontWeight:800, color:"var(--text)", marginBottom:16 }}>🏅 Employee Expense Integrity Ranking</div>
              {(!integrityData || integrityData.length === 0) ? (
                <div style={{ textAlign:"center", padding:30, color:"#9ca3af" }}>No data for selected period</div>
              ) : (
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                    <thead>
                      <tr style={{ borderBottom:"2px solid var(--border)" }}>
                        {["Rank","Employee","Dept","Expenses","Flags","Cash Bills","Late Logs","Corrections"].map(h=>(
                          <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", whiteSpace:"nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {integrityData.map((r,i)=>{
                        const flagRate = r.total_expenses > 0 ? (r.total_flags / r.total_expenses * 100) : 0;
                        const riskColor = flagRate > 30 ? "#ef4444" : flagRate > 15 ? "#f59e0b" : "#10b981";
                        return (
                          <tr key={r.employee_id} style={{ borderBottom:"1px solid var(--border)", background: i%2===0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                            <td style={{ padding:"10px 12px", fontWeight:800, color:"#9ca3af", fontSize:12 }}>#{i+1}</td>
                            <td style={{ padding:"10px 12px", fontWeight:700, color:"var(--text)" }}>{r.employee_name}</td>
                            <td style={{ padding:"10px 12px", color:"#9ca3af", fontSize:12 }}>{r.department || "—"}</td>
                            <td style={{ padding:"10px 12px", textAlign:"center" }}>{r.total_expenses}</td>
                            <td style={{ padding:"10px 12px", textAlign:"center" }}>
                              <span style={{ background:riskColor+"22", color:riskColor, padding:"2px 8px", borderRadius:12, fontSize:12, fontWeight:700 }}>
                                {r.total_flags}
                              </span>
                            </td>
                            <td style={{ padding:"10px 12px", textAlign:"center" }}>
                              <span style={{ color: r.cash_expense_count > 0 ? "#f59e0b" : "#9ca3af" }}>{r.cash_expense_count}</span>
                            </td>
                            <td style={{ padding:"10px 12px", textAlign:"center" }}>
                              <span style={{ color: r.late_log_count > 0 ? "#ef4444" : "#9ca3af" }}>{r.late_log_count}</span>
                            </td>
                            <td style={{ padding:"10px 12px", textAlign:"center" }}>
                              <span style={{ color: r.correction_count > 0 ? "#f59e0b" : "#9ca3af" }}>{r.correction_count}</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Audit Flag Trend */}
            <div style={{ background:"var(--card)", borderRadius:16, padding:24, marginBottom:20, border:"1px solid var(--border)" }}>
              <div style={{ fontSize:15, fontWeight:800, color:"var(--text)", marginBottom:16 }}>📈 Audit Flag Trend (Month-over-Month)</div>
              {(!auditTrendData || auditTrendData.length === 0) ? (
                <div style={{ textAlign:"center", padding:30, color:"#9ca3af" }}>No audit flag data for this period</div>
              ) : (()=>{
                const TYPE_COLORS = ["#7C3AED","#f59e0b","#ef4444","#10b981","#3b82f6","#ec4899"];
                const months = [...new Set(auditTrendData.map(r=>r.month))].sort();
                const types  = [...new Set(auditTrendData.map(r=>r.flag_type))];
                const monthTotals = months.map(m=>({
                  month: m,
                  total: auditTrendData.filter(r=>r.month===m).reduce((s,r)=>s+Number(r.flag_count),0),
                }));
                const maxTotal = Math.max(...monthTotals.map(m=>m.total), 1);

                // SVG bar chart dimensions
                const W=540, H=160, PAD={t:20, r:16, b:30, l:36};
                const iW=W-PAD.l-PAD.r, iH=H-PAD.t-PAD.b;
                const barW = Math.max(Math.floor(iW / Math.max(months.length,1)) - 8, 12);

                return (
                  <div>
                    {/* SVG bar chart */}
                    <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", overflow:"visible", display:"block", marginBottom:16 }}>
                      {/* Grid lines */}
                      {[0, 0.25, 0.5, 0.75, 1].map((f,i) => {
                        const y = PAD.t + iH - f*iH;
                        return (
                          <g key={i}>
                            <line x1={PAD.l} y1={y} x2={W-PAD.r} y2={y} stroke="rgba(255,255,255,0.07)" strokeWidth={1}/>
                            <text x={PAD.l-5} y={y+3.5} textAnchor="end" fontSize={9} fill="#e2e8f0">
                              {f===0 ? "0" : Math.round(maxTotal*f)}
                            </text>
                          </g>
                        );
                      })}
                      {/* Bars */}
                      {monthTotals.map(({month, total}, i) => {
                        const slotW = iW / months.length;
                        const x = PAD.l + i*slotW + slotW/2 - barW/2;
                        const barH = Math.max((total/maxTotal)*iH, 3);
                        const y = PAD.t + iH - barH;
                        return (
                          <g key={month}>
                            <rect x={x} y={y} width={barW} height={barH} rx={3} fill="#7C3AED" opacity={0.9}/>
                            {/* Value label above bar */}
                            <text x={x + barW/2} y={y-4} textAnchor="middle" fontSize={9} fill="#a78bfa" fontWeight="700">{total}</text>
                            {/* Month label below */}
                            <text x={x + barW/2} y={H-5} textAnchor="middle" fontSize={9} fill="#e2e8f0">{month.substring(5)}</text>
                          </g>
                        );
                      })}
                    </svg>

                    {/* Flag type badges */}
                    <div style={{ fontSize:12, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", marginBottom:10 }}>By Flag Type</div>
                    <div style={{ display:"flex", flexWrap:"wrap", gap:10 }}>
                      {types.map((type,ti)=>{
                        const count = auditTrendData.filter(r=>r.flag_type===type).reduce((s,r)=>s+Number(r.flag_count),0);
                        const color = TYPE_COLORS[ti % TYPE_COLORS.length];
                        return (
                          <div key={type} style={{ background:"var(--bg)", borderRadius:10, padding:"8px 14px", border:`1px solid ${color}44`, display:"flex", alignItems:"center", gap:8 }}>
                            <div style={{ width:10, height:10, borderRadius:"50%", background:color, flexShrink:0 }}/>
                            <span style={{ fontSize:12, color:"var(--text)", textTransform:"capitalize" }}>{type.replace(/_/g," ")}</span>
                            <span style={{ fontSize:13, fontWeight:800, color }}>{count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Bill Correction Rate */}
            <div style={{ background:"var(--card)", borderRadius:16, padding:24, border:"1px solid var(--border)" }}>
              <div style={{ fontSize:15, fontWeight:800, color:"var(--text)", marginBottom:16 }}>📝 Bill Correction Rate per Employee</div>
              {(!billCorrData || billCorrData.length === 0) ? (
                <div style={{ textAlign:"center", padding:30, color:"#9ca3af" }}>No correction data</div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                  {billCorrData.slice(0,20).map((r,i)=>{
                    const rate = r.total_expenses > 0 ? (r.correction_count / r.total_expenses * 100) : 0;
                    const color = rate > 20 ? "#ef4444" : rate > 10 ? "#f59e0b" : "#10b981";
                    return (
                      <div key={i}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4, fontSize:12 }}>
                          <span style={{ fontWeight:700, color:"var(--text)" }}>{r.employee_name}</span>
                          <span style={{ color, fontWeight:700 }}>{rate.toFixed(1)}% ({r.correction_count}/{r.total_expenses})</span>
                        </div>
                        <div style={{ height:10, background:"rgba(243,244,246,0.1)", borderRadius:5 }}>
                          <div style={{ height:"100%", width:`${Math.min(rate,100)}%`, background:color, borderRadius:5, transition:"width 0.5s" }}/>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* ── OPERATIONS TAB ───────────────────────────────────────── */}
        {activeTab === "operations" && !insightLoading && (
          <>
            {/* Settlement Aging */}
            <div style={{ background:"var(--card)", borderRadius:16, padding:24, marginBottom:20, border:"1px solid var(--border)" }}>
              <div style={{ fontSize:15, fontWeight:800, color:"var(--text)", marginBottom:16 }}>⏳ Settlement Aging — Pending Trips</div>
              {(!agingData || agingData.length === 0) ? (
                <div style={{ textAlign:"center", padding:30, color:"#9ca3af" }}>No pending trips in this period</div>
              ) : (
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                    <thead>
                      <tr style={{ borderBottom:"2px solid var(--border)" }}>
                        {["Trip","Employee","Submitted","Advance","Days Pending"].map(h=>(
                          <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {agingData.map((r,i)=>{
                        const days = parseFloat(r.days_pending) || 0;
                        const urgencyColor = days > 14 ? "#ef4444" : days > 7 ? "#f59e0b" : "#10b981";
                        return (
                          <tr key={r.id} style={{ borderBottom:"1px solid var(--border)" }}>
                            <td style={{ padding:"10px 12px", fontWeight:700, color:"var(--text)" }}>{r.title || r.destination || "Trip"}</td>
                            <td style={{ padding:"10px 12px", color:"#9ca3af" }}>{r.employee_name}</td>
                            <td style={{ padding:"10px 12px", color:"#9ca3af", fontSize:12 }}>{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}</td>
                            <td style={{ padding:"10px 12px" }}>₹{Number(r.advance_amount||0).toLocaleString()}</td>
                            <td style={{ padding:"10px 12px" }}>
                              <span style={{ background:urgencyColor+"22", color:urgencyColor, padding:"3px 10px", borderRadius:20, fontWeight:700, fontSize:12 }}>
                                {days}d
                              </span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Advance Utilization */}
            <div style={{ background:"var(--card)", borderRadius:16, padding:24, marginBottom:20, border:"1px solid var(--border)" }}>
              <div style={{ fontSize:15, fontWeight:800, color:"var(--text)", marginBottom:16 }}>💰 Advance Utilization Rate</div>
              {(!utilizationData || utilizationData.length === 0) ? (
                <div style={{ textAlign:"center", padding:30, color:"#9ca3af" }}>No settled trip data</div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                  {utilizationData.map((r,i)=>{
                    const advance = parseFloat(r.total_advance) || 0;
                    const spent = parseFloat(r.total_spent) || 0;
                    const util = advance > 0 ? (spent / advance * 100) : 0;
                    const color = util > 110 ? "#ef4444" : util < 70 ? "#f59e0b" : "#10b981";
                    return (
                      <div key={i}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4, fontSize:13 }}>
                          <div>
                            <span style={{ fontWeight:700, color:"var(--text)" }}>{r.name}</span>
                            <span style={{ fontSize:11, color:"#9ca3af", marginLeft:8 }}>{r.department} · {r.trip_count} trips</span>
                          </div>
                          <div style={{ fontSize:12 }}>
                            <span style={{ fontWeight:800, color }}>{util.toFixed(0)}%</span>
                            <span style={{ color:"#9ca3af", marginLeft:6 }}>₹{Number(spent).toLocaleString()} / ₹{Number(advance).toLocaleString()}</span>
                          </div>
                        </div>
                        <div style={{ height:12, background:"rgba(243,244,246,0.1)", borderRadius:6, overflow:"hidden" }}>
                          <div style={{ height:"100%", width:`${Math.min(util,150)}%`, maxWidth:"100%", background:`linear-gradient(90deg,${color}88,${color})`, borderRadius:6, transition:"width 0.5s" }}/>
                        </div>
                        {(r.overspend_trips > 0 || r.underspend_trips > 0) && (
                          <div style={{ fontSize:11, color:"#9ca3af", marginTop:3, display:"flex", gap:12 }}>
                            {r.overspend_trips > 0 && <span style={{ color:"#ef4444" }}>⬆ {r.overspend_trips} over-budget trips</span>}
                            {r.underspend_trips > 0 && <span style={{ color:"#f59e0b" }}>⬇ {r.underspend_trips} under-utilised trips</span>}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Reimbursement Pipeline */}
            <div style={{ background:"var(--card)", borderRadius:16, padding:24, marginBottom:20, border:"1px solid var(--border)" }}>
              <div style={{ fontSize:15, fontWeight:800, color:"var(--text)", marginBottom:16 }}>🔄 Reimbursement Pipeline</div>
              {(!reimbPipeData || reimbPipeData.length === 0) ? (
                <div style={{ textAlign:"center", padding:30, color:"#9ca3af" }}>No reimbursements in pipeline</div>
              ) : (
                <div style={{ overflowX:"auto" }}>
                  <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                    <thead>
                      <tr style={{ borderBottom:"2px solid var(--border)" }}>
                        {["Trip","Employee","Status","Amount","Submitted","Days Waiting"].map(h=>(
                          <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase", whiteSpace:"nowrap" }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {reimbPipeData.map((r,i)=>{
                        const days = parseFloat(r.days_waiting) || 0;
                        const urgencyColor = days > 10 ? "#ef4444" : days > 5 ? "#f59e0b" : "#10b981";
                        const statusColors = { submitted:"#3b82f6", approved:"#10b981", rejected:"#ef4444" };
                        return (
                          <tr key={r.id} style={{ borderBottom:"1px solid var(--border)" }}>
                            <td style={{ padding:"10px 12px", fontWeight:700, color:"var(--text)" }}>{r.title || "—"}</td>
                            <td style={{ padding:"10px 12px", color:"#9ca3af" }}>{r.employee_name}</td>
                            <td style={{ padding:"10px 12px" }}>
                              <span style={{ background:(statusColors[r.reimbursement_status]||"#9ca3af")+"22", color:(statusColors[r.reimbursement_status]||"#9ca3af"), padding:"2px 8px", borderRadius:12, fontSize:11, fontWeight:700, textTransform:"capitalize" }}>
                                {r.reimbursement_status}
                              </span>
                            </td>
                            <td style={{ padding:"10px 12px" }}>₹{Number(r.reimbursement_amount||0).toLocaleString()}</td>
                            <td style={{ padding:"10px 12px", fontSize:12, color:"#9ca3af" }}>{r.reimbursement_submitted_at ? new Date(r.reimbursement_submitted_at).toLocaleDateString() : "—"}</td>
                            <td style={{ padding:"10px 12px" }}>
                              <span style={{ color:urgencyColor, fontWeight:700 }}>{days}d</span>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Manager Response Speed */}
            <div style={{ background:"var(--card)", borderRadius:16, padding:24, border:"1px solid var(--border)" }}>
              <div style={{ fontSize:15, fontWeight:800, color:"var(--text)", marginBottom:16 }}>⚡ Manager Response Speed</div>
              {(!managerPerfData || managerPerfData.length === 0) ? (
                <div style={{ textAlign:"center", padding:30, color:"#9ca3af" }}>No settled trip data</div>
              ) : (
                <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                  {managerPerfData.map((r,i)=>{
                    const maxDays = Math.max(...managerPerfData.map(m=>parseFloat(m.avg_days_to_settle)||0), 1);
                    const days = parseFloat(r.avg_days_to_settle) || 0;
                    const color = days <= 3 ? "#10b981" : days <= 7 ? "#f59e0b" : "#ef4444";
                    return (
                      <div key={i}>
                        <div style={{ display:"flex", justifyContent:"space-between", marginBottom:4, fontSize:13 }}>
                          <span style={{ fontWeight:700, color:"var(--text)" }}>{r.manager_name}</span>
                          <div style={{ fontSize:12, display:"flex", gap:12, color:"#9ca3af" }}>
                            <span>{r.trips_settled} trips</span>
                            <span style={{ color, fontWeight:800 }}>avg {days}d</span>
                          </div>
                        </div>
                        <div style={{ height:12, background:"rgba(243,244,246,0.1)", borderRadius:6 }}>
                          <div style={{ height:"100%", width:`${(days/maxDays)*100}%`, background:`linear-gradient(90deg,${color}88,${color})`, borderRadius:6, transition:"width 0.5s" }}/>
                        </div>
                        <div style={{ fontSize:11, color:"#9ca3af", marginTop:2 }}>Min: {r.min_days}d · Max: {r.max_days}d</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* ── PERFORMANCE TAB ──────────────────────────────────────── */}
        {activeTab === "performance" && !insightLoading && (
          <>
            {/* Trip Purpose Analysis */}
            <div style={{ background:"var(--card)", borderRadius:16, padding:24, marginBottom:20, border:"1px solid var(--border)" }}>
              <div style={{ fontSize:15, fontWeight:800, color:"var(--text)", marginBottom:16 }}>🗺️ Spend by Trip Purpose</div>
              {(!tripPurposeData || tripPurposeData.length === 0) ? (
                <div style={{ textAlign:"center", padding:30, color:"#9ca3af" }}>No trip data</div>
              ) : (()=>{
                const PURPOSE_COLORS = ["#7C3AED","#3b82f6","#10b981","#f59e0b","#ef4444","#ec4899"];
                const totalSpent = tripPurposeData.reduce((s,r)=>s+parseFloat(r.total_spent||0),0);
                return (
                  <div style={{ display:"flex", gap:24, flexWrap:"wrap" }}>
                    {/* Donut */}
                    <div style={{ display:"flex", flexDirection:"column", alignItems:"center", minWidth:180 }}>
                      <svg viewBox="0 0 160 160" style={{ width:160, height:160 }}>
                        {(()=>{
                          let cumPct = 0;
                          return tripPurposeData.map((r,i)=>{
                            const pct = totalSpent > 0 ? parseFloat(r.total_spent)/totalSpent : 0;
                            const startAngle = cumPct * 2 * Math.PI - Math.PI/2;
                            cumPct += pct;
                            const endAngle = cumPct * 2 * Math.PI - Math.PI/2;
                            if (pct < 0.001) return null;
                            const R=65, r2=40, cx=80, cy=80;
                            const x1=cx+R*Math.cos(startAngle), y1=cy+R*Math.sin(startAngle);
                            const x2=cx+R*Math.cos(endAngle),   y2=cy+R*Math.sin(endAngle);
                            const ix1=cx+r2*Math.cos(startAngle), iy1=cy+r2*Math.sin(startAngle);
                            const ix2=cx+r2*Math.cos(endAngle),   iy2=cy+r2*Math.sin(endAngle);
                            const largeArc = pct > 0.5 ? 1 : 0;
                            const color = PURPOSE_COLORS[i % PURPOSE_COLORS.length];
                            return (
                              <path key={i}
                                d={`M ${ix1} ${iy1} L ${x1} ${y1} A ${R} ${R} 0 ${largeArc} 1 ${x2} ${y2} L ${ix2} ${iy2} A ${r2} ${r2} 0 ${largeArc} 0 ${ix1} ${iy1}`}
                                fill={color} opacity={0.9}
                              />
                            );
                          });
                        })()}
                        <text x="80" y="76" textAnchor="middle" fontSize="10" fill="#e2e8f0">Total</text>
                        <text x="80" y="90" textAnchor="middle" fontSize="11" fontWeight="700" fill="#ffffff">₹{(totalSpent/1000).toFixed(0)}K</text>
                      </svg>
                    </div>
                    {/* Table */}
                    <div style={{ flex:1, minWidth:260, overflowX:"auto" }}>
                      <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                        <thead>
                          <tr style={{ borderBottom:"2px solid var(--border)" }}>
                            {["Purpose","Trips","Total Spend","Flags","Avg Days"].map(h=>(
                              <th key={h} style={{ padding:"6px 10px", textAlign:"left", fontSize:11, fontWeight:700, color:"#9ca3af", textTransform:"uppercase" }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {tripPurposeData.map((r,i)=>(
                            <tr key={i} style={{ borderBottom:"1px solid var(--border)" }}>
                              <td style={{ padding:"8px 10px" }}>
                                <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                                  <div style={{ width:10, height:10, borderRadius:"50%", background:PURPOSE_COLORS[i%PURPOSE_COLORS.length], flexShrink:0 }}/>
                                  <span style={{ fontWeight:700, color:"var(--text)" }}>{r.trip_purpose}</span>
                                </div>
                              </td>
                              <td style={{ padding:"8px 10px" }}>{r.trip_count}</td>
                              <td style={{ padding:"8px 10px", fontWeight:700 }}>₹{Number(r.total_spent||0).toLocaleString()}</td>
                              <td style={{ padding:"8px 10px" }}>
                                <span style={{ color: r.total_flags > 0 ? "#f59e0b" : "#9ca3af" }}>{r.total_flags}</span>
                              </td>
                              <td style={{ padding:"8px 10px", color:"#9ca3af" }}>{r.avg_trip_days}d</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Compliance Score Trend */}
            <div style={{ background:"var(--card)", borderRadius:16, padding:24, border:"1px solid var(--border)" }}>
              <div style={{ fontSize:15, fontWeight:800, color:"var(--text)", marginBottom:16 }}>📊 Month-over-Month Compliance Score</div>
              {(!complianceTrend || complianceTrend.length === 0) ? (
                <div style={{ textAlign:"center", padding:30, color:"#9ca3af" }}>No compliance trend data</div>
              ) : (()=>{
                const rows = complianceTrend.map(r=>({
                  month: r.month,
                  score: r.total_trips > 0 ? Math.round(r.clean_trips / r.total_trips * 100) : 0,
                  total: parseInt(r.total_trips),
                  clean: parseInt(r.clean_trips),
                }));
                const W=540, H=180, PAD={t:24,r:24,b:34,l:44};
                const iW=W-PAD.l-PAD.r, iH=H-PAD.t-PAD.b;
                const pts = rows.map((r,i)=>({
                  x: PAD.l + (rows.length===1 ? iW/2 : (i/(rows.length-1))*iW),
                  y: PAD.t + iH - (r.score/100)*iH,
                  score: r.score, month: r.month,
                }));
                const pathD = pts.map((p,i)=>`${i===0?"M":"L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
                const fillD = `${pathD} L${pts[pts.length-1].x},${PAD.t+iH} L${pts[0].x},${PAD.t+iH} Z`;
                const gridY = [0,25,50,75,100].map(v=>({ y:PAD.t+iH-(v/100)*iH, label:`${v}%` }));
                const latestScore = rows[rows.length-1]?.score || 0;
                const scoreColor = latestScore >= 80 ? "#10b981" : latestScore >= 60 ? "#f59e0b" : "#ef4444";
                return (
                  <div>
                    <div style={{ display:"flex", gap:16, marginBottom:20, flexWrap:"wrap" }}>
                      <div style={{ background:"var(--bg)", borderRadius:12, padding:"12px 20px", textAlign:"center" }}>
                        <div style={{ fontSize:28, fontWeight:900, color:scoreColor }}>{latestScore}%</div>
                        <div style={{ fontSize:11, color:"#9ca3af" }}>Latest Compliance</div>
                      </div>
                      <div style={{ background:"var(--bg)", borderRadius:12, padding:"12px 20px", textAlign:"center" }}>
                        <div style={{ fontSize:20, fontWeight:800, color:"var(--text)" }}>{rows.reduce((s,r)=>s+r.total,0)}</div>
                        <div style={{ fontSize:11, color:"#9ca3af" }}>Total Trips</div>
                      </div>
                      <div style={{ background:"var(--bg)", borderRadius:12, padding:"12px 20px", textAlign:"center" }}>
                        <div style={{ fontSize:20, fontWeight:800, color:"#10b981" }}>{rows.reduce((s,r)=>s+r.clean,0)}</div>
                        <div style={{ fontSize:11, color:"#9ca3af" }}>Clean Trips</div>
                      </div>
                    </div>
                    <svg viewBox={`0 0 ${W} ${H}`} style={{ width:"100%", overflow:"visible" }}>
                      <defs>
                        <linearGradient id="compGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor={scoreColor} stopOpacity={0.3}/>
                          <stop offset="100%" stopColor={scoreColor} stopOpacity={0.02}/>
                        </linearGradient>
                      </defs>
                      {gridY.map((g,gi)=>(
                        <g key={gi}>
                          <line x1={PAD.l} y1={g.y} x2={W-PAD.r} y2={g.y} stroke="rgba(255,255,255,0.07)" strokeWidth={1}/>
                          <text x={PAD.l-5} y={g.y+3.5} textAnchor="end" fontSize={9} fill="#e2e8f0">{g.label}</text>
                        </g>
                      ))}
                      <line x1={PAD.l} y1={PAD.t+iH*0.2} x2={W-PAD.r} y2={PAD.t+iH*0.2} stroke="#10b981" strokeWidth={1} strokeDasharray="4,3" opacity={0.5}/>
                      <text x={W-PAD.r+3} y={PAD.t+iH*0.2+3.5} fontSize={9} fill="#10b981" opacity={0.7}>80%</text>
                      <path d={fillD} fill="url(#compGrad)"/>
                      <path d={pathD} fill="none" stroke={scoreColor} strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round"/>
                      {pts.map((p,pi)=>(
                        <g key={pi}>
                          <circle cx={p.x} cy={p.y} r={4} fill={scoreColor} stroke="white" strokeWidth={2}/>
                          <text x={p.x} y={p.y-9} textAnchor="middle" fontSize={9} fill={scoreColor} fontWeight="700">{p.score}%</text>
                          <text x={p.x} y={H-5} textAnchor="middle" fontSize={9} fill="#e2e8f0">{p.month.substring(5)}</text>
                        </g>
                      ))}
                    </svg>
                  </div>
                );
              })()}
            </div>
          </>
        )}

      </div>
    </>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// OET Analytics Content Component
// ══════════════════════════════════════════════════════════════════════════════
const OET_GROUP_COLORS = {
  support_cost:    "#3b82f6",
  additional_cost: "#f59e0b",
  admin_cost:      "#8b5cf6",
  food_cost:       "#10b981",
  vm_expense:      "#6366f1",
  asst_expense:    "#ec4899",
};
const OET_GROUP_LABELS = {
  support_cost:    "Support Services",
  additional_cost: "Additional Expenses",
  admin_cost:      "Admin Charges",
  food_cost:       "Food Expenses",
  vm_expense:      "Venue Manager",
  asst_expense:    "Asst. Venue Manager",
};

function fmtOet(n) {
  const v = parseFloat(n || 0);
  if (v >= 100000) return `₹${(v/100000).toFixed(1)}L`;
  if (v >= 1000)   return `₹${(v/1000).toFixed(1)}K`;
  return `₹${Math.round(v)}`;
}
function fmtOetFull(n) {
  return Number(n || 0).toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 });
}

// Horizontal bar chart for center-wise comparison
function OETHorizontalBar({ data, valueKey, labelKey, color, maxVal }) {
  const max = maxVal || Math.max(...data.map(d => Number(d[valueKey] || 0)), 1);
  return (
    <div>
      {data.map((d, i) => {
        const pct = (Number(d[valueKey] || 0) / max) * 100;
        return (
          <div key={i} style={{ marginBottom: 10 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, marginBottom: 3 }}>
              <span style={{ color: "#d1d5db", fontWeight: 600 }}>{d[labelKey]}</span>
              <span style={{ color, fontWeight: 700 }}>{fmtOet(d[valueKey])}</span>
            </div>
            <div style={{ height: 8, background: "#1e293b", borderRadius: 4, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${pct}%`, background: color, borderRadius: 4, transition: "width 0.6s ease" }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Stacked bar chart for exam-date cost comparison
function OETStackedBars({ byDate }) {
  if (!byDate?.length) return <div style={{ color: "#64748b", fontSize: 13 }}>No exam data</div>;
  const maxV = Math.max(...byDate.map(d => Number(d.total_cost || 0)), 1);
  const W = 560, H = 180, PAD = { t: 10, r: 10, b: 40, l: 60 };
  const iW = W - PAD.l - PAD.r;
  const iH = H - PAD.t - PAD.b;
  const barW = Math.min(40, (iW / byDate.length) - 6);
  const gridY = [0, 0.25, 0.5, 0.75, 1].map(f => ({ y: PAD.t + iH - f * iH, label: f === 0 ? "0" : fmtOet(maxV * f) }));

  return (
    <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", overflow: "visible" }}>
      {gridY.map((g, i) => (
        <g key={i}>
          <line x1={PAD.l} y1={g.y} x2={W - PAD.r} y2={g.y} stroke="rgba(255,255,255,0.07)" strokeWidth={1} />
          <text x={PAD.l - 4} y={g.y + 3.5} textAnchor="end" fontSize={9} fill="#e2e8f0">{g.label}</text>
        </g>
      ))}
      {byDate.map((d, i) => {
        const x = PAD.l + (i / byDate.length) * iW + (iW / byDate.length - barW) / 2;
        const barH = (Number(d.total_cost || 0) / maxV) * iH;
        const y = PAD.t + iH - barH;
        const dateStr = d.exam_date ? new Date(d.exam_date + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "";
        return (
          <g key={i}>
            <rect x={x} y={y} width={barW} height={barH} fill="#6366f1" rx={3} opacity={0.85} />
            <text x={x + barW / 2} y={H - PAD.b + 13} textAnchor="middle" fontSize={9} fill="#e2e8f0">{dateStr}</text>
            {barH > 18 && (
              <text x={x + barW / 2} y={y + 12} textAnchor="middle" fontSize={8} fill="white" fontWeight="700">{fmtOet(d.total_cost)}</text>
            )}
          </g>
        );
      })}
    </svg>
  );
}

function OETAnalyticsContent({ data }) {
  const { summary, by_center, by_date } = data;
  const totalCandidates = Number(summary?.total_candidates || 0);
  const grandTotal      = Number(summary?.grand_total || 0);
  const avgPerCandidate = totalCandidates > 0 ? grandTotal / totalCandidates : 0;

  // Most expensive center + date
  const topCenter = [...(by_center || [])].sort((a, b) => Number(b.total_oet_cost) - Number(a.total_oet_cost))[0];
  const topDate   = [...(by_date   || [])].sort((a, b) => Number(b.total_cost)     - Number(a.total_cost))[0];

  // Insights
  const insights = [];
  if (topCenter) insights.push({ icon: "🏆", label: "Highest Cost Center", value: `${topCenter.center_name} — ${fmtOetFull(topCenter.total_oet_cost)}`, color: "#f59e0b" });
  if (topDate)   insights.push({ icon: "📅", label: "Most Expensive Date",  value: `${new Date(topDate.exam_date + "T00:00:00").toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" })} — ${fmtOetFull(topDate.total_cost)}`, color: "#ef4444" });
  const avgPerExam = Number(summary?.total_sessions) > 0 ? grandTotal / Number(summary.total_sessions) : 0;
  if (avgPerExam) insights.push({ icon: "📊", label: "Avg Cost per Exam Session", value: fmtOetFull(avgPerExam), color: "#3b82f6" });

  // Group totals across all centers
  const groupTotals = ["support_cost","additional_cost","admin_cost","food_cost","vm_expense","asst_expense"].map(k => ({
    key: k,
    label: OET_GROUP_LABELS[k],
    color: OET_GROUP_COLORS[k],
    total: by_center.reduce((s, c) => s + Number(c[k] || 0), 0),
  }));

  const sCard = (color) => ({
    background: "#1e293b", borderRadius: 14, padding: "16px 20px", borderLeft: `4px solid ${color}`,
  });

  return (
    <div>
      {/* ── Summary KPI cards ─────────────────────────── */}
      <div className="an-kpi-grid" style={{ marginBottom: 24 }}>
        {[
          { label: "Total Exam Sessions", value: summary?.total_sessions || 0,   color: "#6366f1", icon: "📋" },
          { label: "Centers Active",      value: summary?.total_centers  || 0,   color: "#3b82f6", icon: "🏫" },
          { label: "Total Candidates",    value: totalCandidates,                color: "#10b981", icon: "👥" },
          { label: "Grand OET Cost",      value: fmtOetFull(grandTotal),         color: "#f59e0b", icon: "💰" },
          { label: "Avg / Candidate",     value: fmtOetFull(avgPerCandidate),    color: "#06b6d4", icon: "👤" },
        ].map(s => (
          <div key={s.label} style={sCard(s.color)}>
            <div style={{ fontSize: 22, marginBottom: 4 }}>{s.icon}</div>
            <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em" }}>{s.label}</div>
            <div style={{ fontSize: 18, fontWeight: 800, color: s.color, marginTop: 4 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* ── Insights row ──────────────────────────────── */}
      {insights.length > 0 && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
          {insights.map((ins, i) => (
            <div key={i} style={{ background: "#1e293b", borderRadius: 12, padding: "12px 18px", display: "flex", alignItems: "center", gap: 12, flex: "1 1 220px" }}>
              <span style={{ fontSize: 24 }}>{ins.icon}</span>
              <div>
                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>{ins.label}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: ins.color, marginTop: 2 }}>{ins.value}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Exam-date wise cost bar chart ─────────────── */}
      <div style={{ background: "#1e293b", borderRadius: 16, padding: "20px 24px", marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#f1f5f9", marginBottom: 16 }}>📅 Total Cost by Exam Date</div>
        {by_date?.length > 0 ? <OETStackedBars byDate={by_date} /> : <div style={{ color: "#64748b", fontSize: 13 }}>No exam date data</div>}
      </div>

      {/* ── Center-wise cost comparison ───────────────── */}
      <div className="an-2col" style={{ marginBottom: 20 }}>
        <div style={{ background: "#1e293b", borderRadius: 16, padding: "20px 24px" }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#f1f5f9", marginBottom: 16 }}>🏫 Center-wise Total OET Cost</div>
          <OETHorizontalBar data={by_center} valueKey="total_oet_cost" labelKey="center_name" color="#6366f1" />
        </div>
        <div style={{ background: "#1e293b", borderRadius: 16, padding: "20px 24px" }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#f1f5f9", marginBottom: 16 }}>👤 Per-Candidate Cost by Center</div>
          <OETHorizontalBar data={by_center} valueKey="cost_per_candidate" labelKey="center_name" color="#06b6d4" />
        </div>
      </div>

      {/* ── Expense group breakdown across all centers ── */}
      <div style={{ background: "#1e293b", borderRadius: 16, padding: "20px 24px", marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#f1f5f9", marginBottom: 16 }}>📊 Expense Group Totals (All Centers Combined)</div>
        <div className="an-kpi-grid">
          {groupTotals.map(g => (
            <div key={g.key} style={sCard(g.color)}>
              <div style={{ fontSize: 11, color: "#9ca3af", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 6 }}>{g.label}</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: g.color }}>{fmtOetFull(g.total)}</div>
              <div style={{ fontSize: 11, color: "#64748b", marginTop: 4 }}>
                {grandTotal > 0 ? `${((g.total / grandTotal) * 100).toFixed(1)}% of total` : "—"}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Per-center detailed breakdown table ──────── */}
      <div style={{ background: "#1e293b", borderRadius: 16, padding: "20px 24px", marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 800, color: "#f1f5f9", marginBottom: 16 }}>🔍 Detailed Center Breakdown</div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#0f172a" }}>
                {["Center","Exams","Candidates","Support","Additional","Admin","Food","VM Expenses","Asst VM","OET Total","Grand Total","Per Candidate"].map(h => (
                  <th key={h} style={{ padding: "10px 12px", textAlign: "left", fontSize: 11, color: "#64748b", fontWeight: 700, textTransform: "uppercase", whiteSpace: "nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {by_center.map((c, i) => {
                const grandTotalRow = Number(c.total_oet_cost||0) + Number(c.vm_expense||0) + Number(c.asst_expense||0);
                const cands = Number(c.total_candidates || 0);
                return (
                  <tr key={i} style={{ borderTop: "1px solid #334155", background: i % 2 === 0 ? "transparent" : "#172033" }}>
                    <td style={{ padding: "10px 12px" }}>
                      <div style={{ fontSize: 11, background: "#312e81", color: "#a5b4fc", borderRadius: 5, padding: "1px 6px", display: "inline-block", marginBottom: 2, fontWeight: 700 }}>{c.center_no}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: "#e2e8f0" }}>{c.center_name}</div>
                    </td>
                    <td style={{ padding: "10px 12px", textAlign: "center", color: "#94a3b8" }}>{c.exam_count}</td>
                    <td style={{ padding: "10px 12px", textAlign: "center", color: "#94a3b8" }}>{cands}</td>
                    <td style={{ padding: "10px 12px", color: "#3b82f6"  }}>{fmtOet(c.support_cost)}</td>
                    <td style={{ padding: "10px 12px", color: "#f59e0b"  }}>{fmtOet(c.additional_cost)}</td>
                    <td style={{ padding: "10px 12px", color: "#8b5cf6"  }}>{fmtOet(c.admin_cost)}</td>
                    <td style={{ padding: "10px 12px", color: "#10b981"  }}>{fmtOet(c.food_cost)}</td>
                    <td style={{ padding: "10px 12px", color: "#6366f1"  }}>{fmtOet(c.vm_expense)}</td>
                    <td style={{ padding: "10px 12px", color: "#ec4899"  }}>{fmtOet(c.asst_expense)}</td>
                    <td style={{ padding: "10px 12px", fontWeight: 700, color: "#f1f5f9" }}>{fmtOet(c.total_oet_cost)}</td>
                    <td style={{ padding: "10px 12px", fontWeight: 800, color: "#f59e0b" }}>{fmtOet(grandTotalRow)}</td>
                    <td style={{ padding: "10px 12px", color: "#06b6d4"  }}>{cands > 0 ? fmtOet(Number(c.total_oet_cost) / cands) : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr style={{ borderTop: "2px solid #334155", background: "#0f172a" }}>
                <td style={{ padding: "10px 12px", fontWeight: 700, color: "#f1f5f9" }}>TOTAL</td>
                <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: 700 }}>{by_center.reduce((s, c) => s + Number(c.exam_count||0), 0)}</td>
                <td style={{ padding: "10px 12px", textAlign: "center", fontWeight: 700 }}>{by_center.reduce((s, c) => s + Number(c.total_candidates||0), 0)}</td>
                {["support_cost","additional_cost","admin_cost","food_cost","vm_expense","asst_expense"].map(k => (
                  <td key={k} style={{ padding: "10px 12px", fontWeight: 700, color: OET_GROUP_COLORS[k] }}>
                    {fmtOet(by_center.reduce((s, c) => s + Number(c[k]||0), 0))}
                  </td>
                ))}
                <td style={{ padding: "10px 12px", fontWeight: 800, color: "#f1f5f9" }}>{fmtOet(grandTotal)}</td>
                <td style={{ padding: "10px 12px", fontWeight: 800, color: "#f59e0b" }}>
                  {fmtOet(by_center.reduce((s, c) => s + Number(c.total_oet_cost||0) + Number(c.vm_expense||0) + Number(c.asst_expense||0), 0))}
                </td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* ── VM vs Asst VM comparison ─────────────────── */}
      {by_center.some(c => Number(c.vm_expense) > 0 || Number(c.asst_expense) > 0) && (
        <div style={{ background: "#1e293b", borderRadius: 16, padding: "20px 24px", marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#f1f5f9", marginBottom: 16 }}>🧾 Manager Expenses by Center</div>
          <div className="an-2col">
            <div>
              <div style={{ fontSize: 12, color: "#6366f1", fontWeight: 700, marginBottom: 10, textTransform: "uppercase" }}>Venue Manager Expenses</div>
              <OETHorizontalBar data={by_center.filter(c => Number(c.vm_expense) > 0)} valueKey="vm_expense" labelKey="center_name" color="#6366f1" />
              {!by_center.some(c => Number(c.vm_expense) > 0) && <div style={{ color: "#64748b", fontSize: 12 }}>No data</div>}
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#ec4899", fontWeight: 700, marginBottom: 10, textTransform: "uppercase" }}>Asst. Venue Manager Expenses</div>
              <OETHorizontalBar data={by_center.filter(c => Number(c.asst_expense) > 0)} valueKey="asst_expense" labelKey="center_name" color="#ec4899" />
              {!by_center.some(c => Number(c.asst_expense) > 0) && <div style={{ color: "#64748b", fontSize: 12 }}>No data</div>}
            </div>
          </div>
        </div>
      )}

      {/* ── Staff vs Food Quantity chart ──────────────── */}
      {by_center.some(c => Number(c.total_staff_count) > 0 || Number(c.total_food_qty) > 0) && (
        <div style={{ background: "#1e293b", borderRadius: 16, padding: "20px 24px", marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#f1f5f9", marginBottom: 4 }}>🍽️ Support Staff vs Food Quantity Ordered — All Centers</div>
          <div style={{ fontSize: 12, color: "#64748b", marginBottom: 20 }}>Compares total support staff deployed against total food quantity ordered per center</div>
          <div style={{ display: "grid", gap: 14 }}>
            {by_center.map((c, i) => {
              const staff   = Number(c.total_staff_count || 0);
              const foodQty = Number(c.total_food_qty    || 0);
              const maxVal  = Math.max(staff, foodQty, 1);
              return (
                <div key={i} style={{ background: "#0f172a", borderRadius: 12, padding: "14px 18px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                    <div>
                      <span style={{ background: "#312e81", color: "#a5b4fc", borderRadius: 5, padding: "1px 7px", fontSize: 11, fontWeight: 700, marginRight: 8 }}>{c.center_no}</span>
                      <span style={{ fontSize: 13, fontWeight: 700, color: "#e2e8f0" }}>{c.center_name}</span>
                    </div>
                    <div style={{ display: "flex", gap: 16, fontSize: 12 }}>
                      <span style={{ color: "#6366f1" }}>👷 {staff} staff</span>
                      <span style={{ color: "#10b981" }}>🍽 {foodQty} qty</span>
                      {staff > 0 && foodQty > 0 && (
                        <span style={{ color: "#94a3b8" }}>ratio {(foodQty / staff).toFixed(1)}x</span>
                      )}
                    </div>
                  </div>
                  {/* Staff bar */}
                  <div style={{ marginBottom: 6 }}>
                    <div style={{ fontSize: 10, color: "#6366f1", fontWeight: 700, marginBottom: 3, textTransform: "uppercase" }}>Support Staff</div>
                    <div style={{ height: 10, background: "#1e293b", borderRadius: 5, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${(staff / maxVal) * 100}%`, background: "#6366f1", borderRadius: 5, transition: "width 0.6s" }} />
                    </div>
                  </div>
                  {/* Food qty bar */}
                  <div>
                    <div style={{ fontSize: 10, color: "#10b981", fontWeight: 700, marginBottom: 3, textTransform: "uppercase" }}>Food Quantity Ordered</div>
                    <div style={{ height: 10, background: "#1e293b", borderRadius: 5, overflow: "hidden" }}>
                      <div style={{ height: "100%", width: `${(foodQty / maxVal) * 100}%`, background: "#10b981", borderRadius: 5, transition: "width 0.6s" }} />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          {/* Summary totals */}
          <div style={{ display: "flex", gap: 20, marginTop: 18, padding: "12px 16px", background: "#0f172a", borderRadius: 10 }}>
            <div><span style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase" }}>Total Staff All Centers</span><div style={{ fontSize: 18, fontWeight: 800, color: "#6366f1" }}>{by_center.reduce((s, c) => s + Number(c.total_staff_count || 0), 0)}</div></div>
            <div><span style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase" }}>Total Food Qty All Centers</span><div style={{ fontSize: 18, fontWeight: 800, color: "#10b981" }}>{by_center.reduce((s, c) => s + Number(c.total_food_qty || 0), 0)}</div></div>
            {(() => {
              const ts = by_center.reduce((s,c) => s+Number(c.total_staff_count||0), 0);
              const tf = by_center.reduce((s,c) => s+Number(c.total_food_qty||0), 0);
              return ts > 0 && tf > 0 ? <div><span style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase" }}>Avg Food Ratio</span><div style={{ fontSize: 18, fontWeight: 800, color: "#f59e0b" }}>{(tf/ts).toFixed(1)}x per staff</div></div> : null;
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
